import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  adminLogin,
  verifyAdminSession,
  getSaltBlock,
  updateSaltBlock,
  getSaltieWinksPage,
  updateSaltieWinksPage,
  getMeetTheHerdPage,
  updateMeetTheHerdPage,
  getLilSaltiesPage,
  updateLilSaltiesPage,
  getSaltieBraysPage,
  updateSaltieBraysPage,
  getSaltieMoosPage,
  updateSaltieMoosPage,
  getFrontPorchPage,
  updateFrontPorchPage,
  getSaltieShoutOut,
  updateSaltieShoutOut,
  uploadSaltieShoutOutPhoto,
  listSaltieShoutOutArchive,
  archiveAndRestartSaltieShoutOut,
  deleteArchivedSaltieShoutOut,
  uploadHearABrayPhoto,
  uploadHearAMooPhoto,
  getPeekAMoo,
  updatePeekAMoo,
  getChapterContent,
  updateChapterContent,
  uploadChapterHero,
  uploadChapterFeaturePhoto,
  getStoryBarnItems,
  createStoryBarnItem,
  updateStoryBarnItem,
  deleteStoryBarnItem,
  getWelcomeEmail,
  updateWelcomeEmail,
  sendWelcomeEmailTest,
  adminListSaltieWinks,
  createSaltieWink,
  updateSaltieWink,
  deleteSaltieWink,
  adminListPhotos,
  adminUploadPhoto,
  adminDeletePhoto,
  adminListCharacterBios,
  updateCharacterBio,
  getSaltieSelfies,
  uploadSaltieSelfie,
  updateSaltieSelfie,
  deleteSaltieSelfie,
  getAudiobook,
  uploadAudiobook,
  updateAudiobookMeta,
  deleteAudiobook,
  getSaltieFunFacts,
  updateSaltieFunFacts,
  getColoringPages,
  uploadColoringPage,
  updateColoringPage,
  deleteColoringPage,
} from "../lib/api";
import { CHAPTERS } from "../lib/data";
import AdminAvailableAnimals from "../components/AdminAvailableAnimals";
import AdminShop from "../components/admin-shop/AdminShop";
import { Lock, Pencil, Trash2, Check, X, Plus, Send, Mail, Camera, Image as ImageIcon, BookOpen, Radio, QrCode, Users } from "lucide-react";
import {
  EditorProvider,
  Editor,
  Toolbar,
  BtnBold,
  BtnItalic,
  BtnUnderline,
  BtnBulletList,
  BtnNumberedList,
  BtnLink,
  BtnClearFormatting,
  Separator,
} from "react-simple-wysiwyg";
import CropEditor from "../components/CropEditor";
import SaltieShoutOutStationery from "../components/SaltieShoutOutStationery";

const STORAGE_KEY = "salty-moo-admin";

const EMPTY_BARN_ITEM = {
  slug: "",
  title: "",
  kind: "",
  note: "",
  status: "coming-soon",
  order: 100,
};

const EMPTY_WINK = {
  wink: "",
  when: "",
  context: "",
  published: true,
};

// Slugs that render in a landscape 4:3 frame — must mirror server.py LANDSCAPE_SLUGS.
const LANDSCAPE_SLUGS = new Set([
  "stefanie-mike",
  "alexis-ethan",
  "tyler-dawson-saylor",
]);

// Must mirror lib/data.js slugs — kept here so the admin UI can show every character
// even before the first photo is uploaded.
const PHOTO_ROSTER = [
  {
    group: "Saltie Brays™",
    items: [
      { slug: "bonnie", name: "Bonnie" },
      { slug: "shadow", name: "Shadow" },
      { slug: "mae-mae", name: "Mae Mae" },
      { slug: "snickers", name: "Snickers" },
    ],
  },
  {
    group: "Saltie Moos™",
    items: [
      { slug: "glory", name: "Glory" },
      { slug: "grace", name: "Grace" },
      { slug: "winnie-the-moo", name: "Winnie the Moo" },
      { slug: "biscuit", name: "Biscuit" },
      { slug: "churro", name: "Churro" },
      { slug: "boomer", name: "Boomer" },
      { slug: "cinnamon", name: "Cinnamon" },
    ],
  },
  {
    group: "Family",
    items: [
      { slug: "stefanie-mike", name: "Stefanie & Mike" },
      { slug: "alexis-ethan", name: "Alexis & Ethan" },
      { slug: "tyler-dawson-saylor", name: "Tyler, Dawson & Saylor" },
    ],
  },
  {
    group: "Saltie Fun Facts — Moos",
    items: [
      { slug: "fun-moo-1", name: "Moo 1" },
      { slug: "fun-moo-2", name: "Moo 2" },
      { slug: "fun-moo-3", name: "Moo 3" },
      { slug: "fun-moo-4", name: "Moo 4" },
      { slug: "fun-moo-5", name: "Moo 5" },
      { slug: "fun-moo-6", name: "Moo 6" },
      { slug: "fun-moo-7", name: "Moo 7" },
    ],
  },
  {
    group: "Saltie Fun Facts — Brays",
    items: [
      { slug: "fun-bray-1", name: "Bray 1" },
      { slug: "fun-bray-2", name: "Bray 2" },
      { slug: "fun-bray-3", name: "Bray 3" },
      { slug: "fun-bray-4", name: "Bray 4" },
    ],
  },
];

export default function Admin() {
  const [passcode, setPasscode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [sb, setSb] = useState(null);
  const [pm, setPm] = useState(null);
  const [savingSb, setSavingSb] = useState(false);
  const [savingPm, setSavingPm] = useState(false);
  const [barnItems, setBarnItems] = useState([]);
  const [editingSlug, setEditingSlug] = useState(null);
  const [draft, setDraft] = useState(EMPTY_BARN_ITEM);
  const [newItem, setNewItem] = useState(EMPTY_BARN_ITEM);
  const [addingBarn, setAddingBarn] = useState(false);
  const [welcome, setWelcome] = useState(null);
  const [savingWelcome, setSavingWelcome] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [winks, setWinks] = useState([]);
  const [newWink, setNewWink] = useState(EMPTY_WINK);
  const [addingWink, setAddingWink] = useState(false);
  const [editingWinkId, setEditingWinkId] = useState(null);
  const [winkDraft, setWinkDraft] = useState(EMPTY_WINK);
  const [photos, setPhotos] = useState({});
  const [uploadingSlug, setUploadingSlug] = useState(null);
  const [cropTarget, setCropTarget] = useState(null); // { slug, name, file } | null
  const [bios, setBios] = useState([]);
  const [bioDrafts, setBioDrafts] = useState({});
  const [savingBio, setSavingBio] = useState(null);
  const [selfies, setSelfies] = useState([]);
  const [uploadingSelfie, setUploadingSelfie] = useState(false);
  const [selfieCaptionDraft, setSelfieCaptionDraft] = useState("");
  const [editingSelfieId, setEditingSelfieId] = useState(null);
  const [selfieDraft, setSelfieDraft] = useState({ caption: "", order: 100 });
  const [audiobook, setAudiobook] = useState(null);
  const [audiobookTitle, setAudiobookTitle] = useState("");
  const [audiobookSubtitle, setAudiobookSubtitle] = useState("");
  const [uploadingAudiobook, setUploadingAudiobook] = useState(false);
  const [savingAudiobookMeta, setSavingAudiobookMeta] = useState(false);
  const [funFacts, setFunFacts] = useState(null);
  const [savingFunFacts, setSavingFunFacts] = useState(false);
  const [coloringPages, setColoringPages] = useState([]);
  const [uploadingColoring, setUploadingColoring] = useState(false);
  const [newColoring, setNewColoring] = useState({ title: "", verse: "", order: 0, file: null });

  // His Story, Our Yes — editable chapter content
  const [hisStory, setHisStory] = useState(null);
  const [savingHisStory, setSavingHisStory] = useState(false);
  const [uploadingHisStoryHero, setUploadingHisStoryHero] = useState(false);
  const [uploadingHisStoryPhoto, setUploadingHisStoryPhoto] = useState(false);

  // Saltie Winks page content — every hardcoded string on the public page
  const [swp, setSwp] = useState(null);
  const [savingSwp, setSavingSwp] = useState(false);
  // Meet the Herd page content
  const [mthp, setMthp] = useState(null);
  const [savingMthp, setSavingMthp] = useState(false);
  // Lil Salties page content
  const [lsp, setLsp] = useState(null);
  const [savingLsp, setSavingLsp] = useState(false);
  // Saltie Brays page content
  const [sbp, setSbp] = useState(null);
  const [savingSbp, setSavingSbp] = useState(false);
  // Saltie Moos page content
  const [smp, setSmp] = useState(null);
  const [savingSmp, setSavingSmp] = useState(false);
  // Front Porch page content
  const [fpp, setFpp] = useState(null);
  const [savingFpp, setSavingFpp] = useState(false);
  // Saltie Shout-Out — the writing-desk editor state
  const [sso, setSso] = useState(null);
  const [savingSso, setSavingSso] = useState(false);
  const [uploadingSsoPhoto, setUploadingSsoPhoto] = useState(false);
  const [previewSso, setPreviewSso] = useState(false);
  // Archive of past letters (readable from the same admin desk)
  const [ssoArchive, setSsoArchive] = useState([]);
  const [archivingSso, setArchivingSso] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      verifyAdminSession(stored)
        .then(() => {
          setPasscode(stored);
          setAuthed(true);
        })
        .catch(() => sessionStorage.removeItem(STORAGE_KEY));
    }
    // Admin should NOT be indexed by search engines
    document.title = "Admin · The Salty Moo™";
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex, nofollow");
    return () => {
      robots.setAttribute("content", "index, follow");
    };
  }, []);

  useEffect(() => {
    if (!authed) return;
    getSaltBlock().then(setSb);
    getPeekAMoo().then(setPm);
    loadBarnItems();
    loadWinks();
    loadPhotos();
    loadBios();
    loadSelfies();
    loadAudiobook();
    getSaltieFunFacts().then(setFunFacts).catch(() => {});
    getColoringPages().then(setColoringPages).catch(() => {});
    getWelcomeEmail(passcode).then(setWelcome).catch(() => {});
    getChapterContent("his-story").then(setHisStory).catch(() => {});
    getSaltieWinksPage().then(setSwp).catch(() => {});
    getMeetTheHerdPage().then(setMthp).catch(() => {});
    getLilSaltiesPage().then(setLsp).catch(() => {});
    getSaltieBraysPage().then(setSbp).catch(() => {});
    getSaltieMoosPage().then(setSmp).catch(() => {});
    getFrontPorchPage().then(setFpp).catch(() => {});
    getSaltieShoutOut().then(setSso).catch(() => {});
    listSaltieShoutOutArchive()
      .then((d) => setSsoArchive(Array.isArray(d?.letters) ? d.letters : []))
      .catch(() => setSsoArchive([]));
  }, [authed]);

  const loadBarnItems = async () => {
    try {
      const list = await getStoryBarnItems();
      setBarnItems(list);
    } catch {
      // ignore — fresh DB may be empty
    }
  };

  const loadWinks = async () => {
    try {
      const list = await adminListSaltieWinks(passcode);
      setWinks(list);
    } catch {
      // ignore
    }
  };

  const addWink = async (e) => {
    e.preventDefault();
    if (!newWink.wink.trim() || !newWink.when.trim()) {
      toast("A wink and a date are required.");
      return;
    }
    setAddingWink(true);
    try {
      await createSaltieWink(newWink, passcode);
      toast("Saltie Wink added.");
      setNewWink(EMPTY_WINK);
      loadWinks();
    } catch {
      toast("Couldn't save that wink.");
    } finally {
      setAddingWink(false);
    }
  };

  const startEditWink = (w) => {
    setEditingWinkId(w.id);
    setWinkDraft({ ...w });
  };
  const cancelEditWink = () => {
    setEditingWinkId(null);
    setWinkDraft(EMPTY_WINK);
  };
  const saveEditWink = async () => {
    try {
      await updateSaltieWink(editingWinkId, winkDraft, passcode);
      toast("Wink updated.");
      cancelEditWink();
      loadWinks();
    } catch {
      toast("Couldn't update wink.");
    }
  };
  const removeWink = async (id) => {
    if (!window.confirm("Remove this wink from the scrapbook?")) return;
    try {
      await deleteSaltieWink(id, passcode);
      toast("Wink removed.");
      loadWinks();
    } catch {
      toast("Couldn't remove wink.");
    }
  };

  const loadPhotos = async () => {
    try {
      const list = await adminListPhotos(passcode);
      const map = {};
      list.forEach((p) => {
        map[p.slug] = p;
      });
      setPhotos(map);
      // reset the module-level cache so BioCards/MeetTheFamily see the change
      const mod = await import("../hooks/useCharacterPhoto");
      mod.invalidateCharacterPhotos && mod.invalidateCharacterPhotos();
    } catch {
      // ignore — admin may not be authed yet
    }
  };

  const loadBios = async () => {
    try {
      const list = await adminListCharacterBios(passcode);
      setBios(list);
      const drafts = {};
      list.forEach((b) => {
        drafts[b.slug] = { role: b.role || "", intro: b.intro || "" };
      });
      setBioDrafts(drafts);
    } catch {
      // ignore
    }
  };

  const saveBio = async (slug) => {
    setSavingBio(slug);
    try {
      await updateCharacterBio(slug, bioDrafts[slug], passcode);
      toast("Bio saved.");
      loadBios();
    } catch {
      toast("Couldn't save that bio.");
    } finally {
      setSavingBio(null);
    }
  };

  const loadSelfies = async () => {
    try {
      const list = await getSaltieSelfies();
      setSelfies(list);
    } catch {
      // ignore
    }
  };

  const uploadSelfie = async (file) => {
    if (!file) return;
    setUploadingSelfie(true);
    try {
      const nextOrder = selfies.length
        ? Math.max(...selfies.map((s) => s.order || 0)) + 10
        : 100;
      await uploadSaltieSelfie(
        file,
        { caption: selfieCaptionDraft, order: nextOrder },
        passcode
      );
      toast("Saltie Selfie added.");
      setSelfieCaptionDraft("");
      loadSelfies();
    } catch {
      toast("Couldn't upload that selfie.");
    } finally {
      setUploadingSelfie(false);
    }
  };

  const startEditSelfie = (s) => {
    setEditingSelfieId(s.id);
    setSelfieDraft({ caption: s.caption || "", order: s.order || 100 });
  };
  const cancelEditSelfie = () => {
    setEditingSelfieId(null);
    setSelfieDraft({ caption: "", order: 100 });
  };
  const saveEditSelfie = async () => {
    try {
      await updateSaltieSelfie(
        editingSelfieId,
        { caption: selfieDraft.caption, order: Number(selfieDraft.order) || 100 },
        passcode
      );
      toast("Selfie updated.");
      cancelEditSelfie();
      loadSelfies();
    } catch {
      toast("Couldn't update selfie.");
    }
  };
  const removeSelfie = async (id) => {
    if (!window.confirm("Remove this Saltie Selfie?")) return;
    try {
      await deleteSaltieSelfie(id, passcode);
      toast("Selfie removed.");
      loadSelfies();
    } catch {
      toast("Couldn't remove selfie.");
    }
  };

  const loadAudiobook = async () => {
    try {
      const data = await getAudiobook();
      setAudiobook(data);
      setAudiobookTitle(data.title || "");
      setAudiobookSubtitle(data.subtitle || "");
    } catch {
      // ignore
    }
  };

  const handleAudiobookUpload = async (file) => {
    if (!file) return;
    setUploadingAudiobook(true);
    try {
      await uploadAudiobook(
        file,
        { title: audiobookTitle, subtitle: audiobookSubtitle },
        passcode
      );
      toast("Audiobook uploaded.");
      loadAudiobook();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed.";
      toast(detail);
    } finally {
      setUploadingAudiobook(false);
    }
  };

  const saveAudiobookMeta = async () => {
    setSavingAudiobookMeta(true);
    try {
      await updateAudiobookMeta(
        { title: audiobookTitle, subtitle: audiobookSubtitle },
        passcode
      );
      toast("Audiobook details saved.");
      loadAudiobook();
    } catch {
      toast("Couldn't save audiobook details.");
    } finally {
      setSavingAudiobookMeta(false);
    }
  };

  const removeAudiobook = async () => {
    if (!window.confirm("Remove the audiobook file? The player will return to Coming Soon state.")) return;
    try {
      await deleteAudiobook(passcode);
      toast("Audiobook removed.");
      loadAudiobook();
    } catch {
      toast("Couldn't remove audiobook.");
    }
  };

  const commitCroppedPhoto = async (blob) => {
    if (!cropTarget) return;
    const { slug, name } = cropTarget;
    setUploadingSlug(slug);
    try {
      const file = new File([blob], `${slug}.jpg`, { type: "image/jpeg" });
      await adminUploadPhoto(slug, file, passcode, { preCropped: true });
      toast(`Photo saved for ${name}.`);
      setCropTarget(null);
      loadPhotos();
    } catch (err) {
      toast(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploadingSlug(null);
    }
  };

  const removePhoto = async (slug) => {
    if (!window.confirm(`Remove the photo for ${slug}?`)) return;
    try {
      await adminDeletePhoto(slug, passcode);
      toast("Photo removed.");
      loadPhotos();
    } catch {
      toast("Couldn't remove photo.");
    }
  };

  const startEdit = (item) => {
    setEditingSlug(item.slug);
    setDraft({ ...item });
  };
  const cancelEdit = () => {
    setEditingSlug(null);
    setDraft(EMPTY_BARN_ITEM);
  };
  const saveEdit = async () => {
    try {
      await updateStoryBarnItem(
        editingSlug,
        { ...draft, order: Number(draft.order) || 100 },
        passcode
      );
      toast("Item updated.");
      cancelEdit();
      loadBarnItems();
    } catch {
      toast("Couldn't save item.");
    }
  };
  const removeBarnItem = async (slug) => {
    if (!window.confirm(`Remove "${slug}" from the Story Barn?`)) return;
    try {
      await deleteStoryBarnItem(slug, passcode);
      toast("Item removed.");
      loadBarnItems();
    } catch {
      toast("Couldn't remove item.");
    }
  };
  const addBarnItem = async (e) => {
    e.preventDefault();
    if (!newItem.slug || !newItem.title) {
      toast("Slug and title are required.");
      return;
    }
    setAddingBarn(true);
    try {
      await createStoryBarnItem(
        { ...newItem, order: Number(newItem.order) || 100 },
        passcode
      );
      toast(`"${newItem.title}" added to the Story Barn.`);
      setNewItem(EMPTY_BARN_ITEM);
      loadBarnItems();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast(detail === "Slug already exists" ? "That slug is taken." : "Couldn't add item.");
    } finally {
      setAddingBarn(false);
    }
  };

  const saveWelcome = async (e) => {
    e.preventDefault();
    setSavingWelcome(true);
    try {
      const saved = await updateWelcomeEmail(welcome, passcode);
      setWelcome(saved);
      toast("Welcome email updated.");
    } catch {
      toast("Couldn't save the welcome email.");
    } finally {
      setSavingWelcome(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast("Enter a real email address first.");
      return;
    }
    setSendingTest(true);
    try {
      await sendWelcomeEmailTest(testEmail, passcode);
      toast(`Test email sent to ${testEmail}.`);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Send failed.";
      toast(detail);
    } finally {
      setSendingTest(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await adminLogin(passcode);
      sessionStorage.setItem(STORAGE_KEY, res.token);
      setPasscode(res.token);
      setAuthed(true);
      toast("Welcome back to the pasture, friend.");
    } catch (err) {
      toast(err?.response?.status === 429 ? err.response.data.detail : "That passcode didn't open the gate.");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPasscode("");
  };

  const saveSb = async (e) => {
    e.preventDefault();
    setSavingSb(true);
    try {
      await updateSaltBlock(sb, passcode);
      toast("Salt Block saved.");
    } catch {
      toast("Couldn't save Salt Block.");
    } finally {
      setSavingSb(false);
    }
  };

  const savePm = async (e) => {
    e.preventDefault();
    setSavingPm(true);
    try {
      await updatePeekAMoo(
        {
          ...pm,
          position_x: Number(pm.position_x),
          position_y: Number(pm.position_y),
        },
        passcode
      );
      toast("Peek-a-Moo has moved to a new hiding spot.");
    } catch {
      toast("Couldn't save Peek-a-Moo.");
    } finally {
      setSavingPm(false);
    }
  };

  const saveHisStory = async (e) => {
    e.preventDefault();
    if (!hisStory) return;
    setSavingHisStory(true);
    try {
      const fresh = await updateChapterContent(
        "his-story",
        {
          title: hisStory.title || "",
          subtitle: hisStory.subtitle || "",
          intro: hisStory.intro || "",
          body_html: hisStory.body_html || "",
          featured_quote: hisStory.featured_quote || "",
          button_text: hisStory.button_text || "",
          feature_photo_caption: hisStory.feature_photo_caption || "",
          // hero_image_url + feature_photo_url intentionally omitted —
          // upload endpoints own those fields.
        },
        passcode,
      );
      setHisStory(fresh);
      toast("His Story, Our Yes saved.");
    } catch {
      toast("Couldn't save His Story.");
    } finally {
      setSavingHisStory(false);
    }
  };

  const uploadHisStoryHero = async (file) => {
    if (!file) return;
    setUploadingHisStoryHero(true);
    try {
      const res = await uploadChapterHero("his-story", file, passcode);
      setHisStory((h) => ({ ...(h || {}), hero_image_url: res.url }));
      toast("New hero image saved.");
    } catch {
      toast("Couldn't upload that hero image.");
    } finally {
      setUploadingHisStoryHero(false);
    }
  };

  const clearHisStoryHero = async () => {
    if (!window.confirm("Revert to the bundled hero image?")) return;
    try {
      const fresh = await updateChapterContent(
        "his-story",
        { hero_image_url: "" },
        passcode,
      );
      setHisStory(fresh);
      toast("Reverted to the default hero image.");
    } catch {
      toast("Couldn't revert the hero image.");
    }
  };

  const uploadHisStoryPhoto = async (file) => {
    if (!file) return;
    setUploadingHisStoryPhoto(true);
    try {
      const res = await uploadChapterFeaturePhoto("his-story", file, passcode);
      setHisStory((h) => ({ ...(h || {}), feature_photo_url: res.url }));
      toast("Photo saved. The placeholder is now replaced.");
    } catch {
      toast("Couldn't upload that photo.");
    } finally {
      setUploadingHisStoryPhoto(false);
    }
  };

  const clearHisStoryPhoto = async () => {
    if (!window.confirm("Remove the photo and show the \u201Ccoming soon\u201D placeholder again?")) return;
    try {
      const fresh = await updateChapterContent(
        "his-story",
        { feature_photo_url: "" },
        passcode,
      );
      setHisStory(fresh);
      toast("Placeholder is back.");
    } catch {
      toast("Couldn't remove the photo.");
    }
  };

  const saveSwp = async (e) => {
    e.preventDefault();
    if (!swp) return;
    setSavingSwp(true);
    try {
      const fresh = await updateSaltieWinksPage(swp, passcode);
      setSwp(fresh);
      toast("Saltie Winks page saved.");
    } catch {
      toast("Couldn't save the Saltie Winks page.");
    } finally {
      setSavingSwp(false);
    }
  };

  const saveMthp = async (e) => {
    e.preventDefault();
    if (!mthp) return;
    setSavingMthp(true);
    try {
      const fresh = await updateMeetTheHerdPage(mthp, passcode);
      setMthp(fresh);
      toast("Meet the Herd page saved.");
    } catch {
      toast("Couldn't save the Meet the Herd page.");
    } finally {
      setSavingMthp(false);
    }
  };

  const saveLsp = async (e) => {
    e.preventDefault();
    if (!lsp) return;
    setSavingLsp(true);
    try {
      const fresh = await updateLilSaltiesPage(lsp, passcode);
      setLsp(fresh);
      toast("Lil Salties page saved.");
    } catch {
      toast("Couldn't save the Lil Salties page.");
    } finally {
      setSavingLsp(false);
    }
  };

  const saveSbp = async (e) => {
    e.preventDefault();
    if (!sbp) return;
    setSavingSbp(true);
    try {
      const fresh = await updateSaltieBraysPage(sbp, passcode);
      setSbp(fresh);
      toast("Saltie Brays page saved.");
    } catch {
      toast("Couldn't save the Saltie Brays page.");
    } finally {
      setSavingSbp(false);
    }
  };

  const saveSmp = async (e) => {
    e.preventDefault();
    if (!smp) return;
    setSavingSmp(true);
    try {
      const fresh = await updateSaltieMoosPage(smp, passcode);
      setSmp(fresh);
      toast("Saltie Moos page saved.");
    } catch {
      toast("Couldn't save the Saltie Moos page.");
    } finally {
      setSavingSmp(false);
    }
  };

  const saveFpp = async (e) => {
    e.preventDefault();
    if (!fpp) return;
    setSavingFpp(true);
    try {
      const fresh = await updateFrontPorchPage(fpp, passcode);
      setFpp(fresh);
      toast("Front Porch page saved.");
    } catch {
      toast("Couldn't save the Front Porch page.");
    } finally {
      setSavingFpp(false);
    }
  };

  // "Publish my Saltie Shout-Out" — writes a new featured letter.
  const publishSso = async (e) => {
    e.preventDefault();
    if (!sso) return;
    if (!sso.recipient?.trim()) {
      toast("Who is this Saltie Shout-Out for?");
      return;
    }
    if (!sso.body?.trim()) {
      toast("Your letter is empty. Say a few kind words.");
      return;
    }
    setSavingSso(true);
    try {
      const fresh = await updateSaltieShoutOut(sso, passcode);
      setSso(fresh);
      toast("Your Saltie Shout-Out is published.");
    } catch {
      toast("Couldn't publish that Saltie Shout-Out.");
    } finally {
      setSavingSso(false);
    }
  };

  // Optional keepsake photo upload — stored on the same shout-out doc.
  const onSsoPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSsoPhoto(true);
    try {
      const { url } = await uploadSaltieShoutOutPhoto(file, passcode);
      setSso((prev) => ({ ...(prev || {}), photo_url: url }));
      toast("Keepsake photo tucked in.");
    } catch {
      toast("Couldn't upload that photo.");
    } finally {
      setUploadingSsoPhoto(false);
      // reset the input so the same file can be re-selected later
      e.target.value = "";
    }
  };

  const clearSsoPhoto = () => {
    setSso((prev) => ({ ...(prev || {}), photo_url: null }));
  };

  // "Archive & start a new letter" — the current featured letter is
  // stamped with a published_at timestamp and moved into the archive
  // collection, then the writing desk resets to a fresh blank sheet.
  const archiveAndRestart = async () => {
    const hasContent = sso?.recipient?.trim() && sso?.body?.trim();
    const message = hasContent
      ? "Archive the letter you're writing now and start a fresh one? The current letter will be kept safely in the keepsake book."
      : "Reset the writing desk to a fresh blank sheet?";
    if (!window.confirm(message)) return;
    setArchivingSso(true);
    try {
      const res = await archiveAndRestartSaltieShoutOut(passcode);
      setSso(res.featured || null);
      const fresh = await listSaltieShoutOutArchive();
      setSsoArchive(Array.isArray(fresh?.letters) ? fresh.letters : []);
      toast(
        hasContent
          ? "Letter tucked into the keepsake book. Fresh sheet ready."
          : "Fresh sheet ready."
      );
    } catch {
      toast("Couldn't archive the letter.");
    } finally {
      setArchivingSso(false);
    }
  };

  const deleteArchivedSso = async (letter) => {
    if (
      !window.confirm(
        `Permanently remove the letter to ${letter.recipient} from the archive? This can't be undone.`
      )
    ) {
      return;
    }
    try {
      await deleteArchivedSaltieShoutOut(letter.id, passcode);
      setSsoArchive((prev) => prev.filter((l) => l.id !== letter.id));
      toast("Removed from the archive.");
    } catch {
      toast("Couldn't remove that letter.");
    }
  };

  if (!authed) {
    return (
      <div className="parchment min-h-screen flex items-center justify-center px-6">
        <form
          onSubmit={handleLogin}
          className="parchment-deep page-shadow max-w-md w-full p-10 rounded-md border border-[#E7DFD5] text-center"
        >
          <Lock className="mx-auto text-[#4B3A2F] mb-3" size={28} />
          <h1 className="font-display text-3xl text-[#4B3A2F] mb-2">
            The Quiet Gate
          </h1>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            for the storyteller only
          </p>
          <input
            data-testid="admin-passcode-input"
            type="password"
            placeholder="passcode"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            className="w-full px-5 py-3 rounded-full bg-[#F9F6F0] border border-[#E7DFD5] focus:outline-none focus:border-[#4B3A2F] font-body text-center text-[#2C2724]"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            className="btn-pasture mt-5 w-full justify-center"
          >
            Open the gate
          </button>
        </form>
      </div>
    );
  }

  if (!sb || !pm) {
    return (
      <div className="parchment min-h-screen flex items-center justify-center">
        <p className="font-script text-2xl text-[#4B3A2F]">loading...</p>
      </div>
    );
  }

  const inputCls =
    "w-full px-4 py-3 rounded-md bg-[#F9F6F0] border border-[#E7DFD5] focus:outline-none focus:border-[#4B3A2F] font-body text-[#2C2724]";
  const labelCls = "text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-2 block";

  return (
    <div className="parchment min-h-screen pt-20 pb-20 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[#4B3A2F] mb-2">
              Storyteller's Desk
            </p>
            <h1 className="font-display text-5xl text-[#4B3A2F]">Admin</h1>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="text-sm text-[#6D5A50] hover:text-[#4B3A2F] underline-offset-4 hover:underline transition-colors"
          >
            sign out
          </button>
        </div>

        {/* Salt Block form */}
        <form
          onSubmit={saveSb}
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mb-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            The Salt Block
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            this month's quiet word
          </p>

          <div className="space-y-5">
            <div>
              <label className={labelCls}>Month label</label>
              <input
                data-testid="admin-input-month_label"
                className={inputCls}
                value={sb.month_label}
                onChange={(e) => setSb({ ...sb, month_label: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Scripture</label>
              <textarea
                data-testid="admin-input-scripture"
                rows={4}
                className={inputCls}
                value={sb.scripture}
                onChange={(e) => setSb({ ...sb, scripture: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Scripture reference</label>
              <input
                data-testid="admin-input-scripture_reference"
                className={inputCls}
                value={sb.scripture_reference}
                onChange={(e) =>
                  setSb({ ...sb, scripture_reference: e.target.value })
                }
              />
            </div>
            <div>
              <label className={labelCls}>Reflection</label>
              <textarea
                data-testid="admin-input-reflection"
                rows={5}
                className={inputCls}
                value={sb.reflection}
                onChange={(e) => setSb({ ...sb, reflection: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Prayer</label>
              <textarea
                data-testid="admin-input-prayer"
                rows={4}
                className={inputCls}
                value={sb.prayer}
                onChange={(e) => setSb({ ...sb, prayer: e.target.value })}
              />
            </div>
            <div>
              <label className={labelCls}>Go Be the Salt</label>
              <textarea
                data-testid="admin-input-go_be_the_salt"
                rows={3}
                className={inputCls}
                value={sb.go_be_the_salt}
                onChange={(e) =>
                  setSb({ ...sb, go_be_the_salt: e.target.value })
                }
              />
            </div>
          </div>

          <button
            data-testid="admin-save-saltblock-btn"
            type="submit"
            disabled={savingSb}
            className="btn-pasture mt-8 disabled:opacity-60"
          >
            {savingSb ? "Saving..." : "Save the Salt Block"}
          </button>
        </form>

        {/* Peek-a-Moo form */}
        <form
          onSubmit={savePm}
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mb-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            Peek-a-Moo™
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            choose where the little wonder is hiding
          </p>

          <div className="space-y-5">
            <div>
              <label className={labelCls}>Hiding on page</label>
              <select
                data-testid="admin-input-page"
                className={inputCls}
                value={pm.page}
                onChange={(e) => setPm({ ...pm, page: e.target.value })}
              >
                {CHAPTERS.map((c) => {
                  const slug = c.path.replace(/^\//, "") || "home";
                  return (
                    <option key={slug} value={slug}>
                      {c.label}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>X position (%)</label>
                <input
                  data-testid="admin-input-position_x"
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  className={inputCls}
                  value={pm.position_x}
                  onChange={(e) =>
                    setPm({ ...pm, position_x: e.target.value })
                  }
                />
              </div>
              <div>
                <label className={labelCls}>Y position (%)</label>
                <input
                  data-testid="admin-input-position_y"
                  type="number"
                  min={0}
                  max={100}
                  step="0.1"
                  className={inputCls}
                  value={pm.position_y}
                  onChange={(e) =>
                    setPm({ ...pm, position_y: e.target.value })
                  }
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>Clue (shown on hover)</label>
              <input
                data-testid="admin-input-clue"
                className={inputCls}
                value={pm.clue}
                onChange={(e) => setPm({ ...pm, clue: e.target.value })}
              />
            </div>
          </div>

          <button
            data-testid="admin-save-peekamoo-btn"
            type="submit"
            disabled={savingPm}
            className="btn-pasture mt-8 disabled:opacity-60"
          >
            {savingPm ? "Hiding..." : "Move Peek-a-Moo"}
          </button>
        </form>

        {/* His Story, Our Yes — editable chapter content */}
        <form
          onSubmit={saveHisStory}
          data-testid="admin-his-story-form"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mb-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            His Story, Our Yes
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            the origin story of the pasture
          </p>

          {!hisStory ? (
            <p className="text-sm text-[#6D5A50] italic">
              loading the pasture&rsquo;s first chapter&hellip;
            </p>
          ) : (
            <div className="space-y-6">
              {/* Hero image */}
              <div>
                <label className={labelCls}>
                  Hero / background image
                  <span className="ml-2 normal-case tracking-normal text-[#6D5A50]">
                    (auto-cropped to 1920&times;800 so the layout never shifts)
                  </span>
                </label>
                <div className="flex flex-wrap items-center gap-4">
                  <div
                    data-testid="admin-his-story-hero-preview"
                    className="w-56 aspect-[12/5] rounded-md border border-[#E7DFD5] overflow-hidden bg-[#F3EBDD] flex items-center justify-center"
                  >
                    <img
                      src={
                        hisStory.hero_image_url
                          ? (hisStory.hero_image_url.startsWith("http")
                              ? hisStory.hero_image_url
                              : `${process.env.REACT_APP_BACKEND_URL || ""}${hisStory.hero_image_url}`)
                          : "/assets/heroes/his-story.webp"
                      }
                      alt="His Story hero preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      className="btn-pasture inline-flex items-center gap-2 cursor-pointer disabled:opacity-60"
                      style={{ pointerEvents: uploadingHisStoryHero ? "none" : "auto" }}
                    >
                      <ImageIcon size={14} />
                      {uploadingHisStoryHero ? "Uploading…" : "Upload new hero"}
                      <input
                        data-testid="admin-his-story-hero-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          uploadHisStoryHero(f);
                        }}
                      />
                    </label>
                    {hisStory.hero_image_url && (
                      <button
                        data-testid="admin-his-story-hero-reset"
                        type="button"
                        onClick={clearHisStoryHero}
                        className="text-xs uppercase tracking-[0.25em] text-[#A87149] hover:text-[#4B3A2F] underline-offset-4 hover:underline transition-colors"
                      >
                        revert to default hero
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Title + subtitle */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Chapter title</label>
                  <input
                    data-testid="admin-his-story-title"
                    className={inputCls}
                    value={hisStory.title || ""}
                    onChange={(e) =>
                      setHisStory({ ...hisStory, title: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Subtitle{" "}
                    <span className="normal-case tracking-normal text-[#6D5A50]">
                      (script line under title)
                    </span>
                  </label>
                  <input
                    data-testid="admin-his-story-subtitle"
                    className={inputCls}
                    value={hisStory.subtitle || ""}
                    onChange={(e) =>
                      setHisStory({ ...hisStory, subtitle: e.target.value })
                    }
                  />
                </div>
              </div>

              {/* Intro paragraph */}
              <div>
                <label className={labelCls}>
                  Introductory paragraph{" "}
                  <span className="normal-case tracking-normal text-[#6D5A50]">
                    (italic script line beneath the parchment header)
                  </span>
                </label>
                <textarea
                  data-testid="admin-his-story-intro"
                  rows={3}
                  className={inputCls}
                  value={hisStory.intro || ""}
                  onChange={(e) =>
                    setHisStory({ ...hisStory, intro: e.target.value })
                  }
                />
              </div>

              {/* Featured quote */}
              <div>
                <label className={labelCls}>
                  Featured quote or Scripture{" "}
                  <span className="normal-case tracking-normal text-[#6D5A50]">
                    (optional — rendered as a large italic pull-quote near the top; leave blank to hide)
                  </span>
                </label>
                <textarea
                  data-testid="admin-his-story-featured-quote"
                  rows={2}
                  className={inputCls}
                  value={hisStory.featured_quote || ""}
                  onChange={(e) =>
                    setHisStory({ ...hisStory, featured_quote: e.target.value })
                  }
                />
              </div>

              {/* Body rich-text */}
              <div>
                <label className={labelCls}>
                  Main body text{" "}
                  <span className="normal-case tracking-normal text-[#6D5A50]">
                    (bold, italic, links, bullets — highlight text to format)
                  </span>
                </label>
                <div
                  data-testid="admin-his-story-body-wysiwyg"
                  className="rounded-md border border-[#E7DFD5] bg-[#FBF7F0] overflow-hidden focus-within:border-[#B08A5A]"
                >
                  <EditorProvider>
                    <Editor
                      data-testid="admin-his-story-body"
                      value={hisStory.body_html || ""}
                      onChange={(e) =>
                        setHisStory({ ...hisStory, body_html: e.target.value })
                      }
                      containerProps={{
                        style: {
                          background: "#FBF7F0",
                          border: "none",
                          minHeight: "320px",
                          fontFamily: "Georgia, 'Times New Roman', serif",
                          fontSize: "15px",
                          lineHeight: "1.7",
                          color: "#2C2724",
                        },
                      }}
                    >
                      <Toolbar
                        style={{
                          background: "#F3EBDD",
                          borderBottom: "1px solid #E7DFD5",
                        }}
                      >
                        <BtnBold />
                        <BtnItalic />
                        <BtnUnderline />
                        <Separator />
                        <BtnBulletList />
                        <BtnNumberedList />
                        <Separator />
                        <BtnLink />
                        <BtnClearFormatting />
                      </Toolbar>
                    </Editor>
                  </EditorProvider>
                </div>
              </div>

              {/* Button text */}
              <div>
                <label className={labelCls}>
                  Chapter-turn button text{" "}
                  <span className="normal-case tracking-normal text-[#6D5A50]">
                    (the button at the bottom that turns to the next chapter)
                  </span>
                </label>
                <input
                  data-testid="admin-his-story-button-text"
                  className={inputCls}
                  value={hisStory.button_text || ""}
                  onChange={(e) =>
                    setHisStory({ ...hisStory, button_text: e.target.value })
                  }
                />
              </div>

              {/* In-body Polaroid photo (currently "coming soon") */}
              <div className="pt-4 border-t border-[#E7DFD5]">
                <label className={labelCls}>
                  In-body photo{" "}
                  <span className="normal-case tracking-normal text-[#6D5A50]">
                    (the Polaroid near the bottom of the page — auto-cropped to 4:3 so the frame never shifts. Empty = show the &ldquo;coming soon&rdquo; placeholder.)
                  </span>
                </label>
                <div className="flex flex-wrap items-center gap-4">
                  <div
                    data-testid="admin-his-story-photo-preview"
                    className="w-48 aspect-[4/3] rounded-md border border-[#E7DFD5] overflow-hidden bg-[#F3EBDD] flex items-center justify-center"
                  >
                    {hisStory.feature_photo_url ? (
                      <img
                        src={
                          hisStory.feature_photo_url.startsWith("http")
                            ? hisStory.feature_photo_url
                            : `${process.env.REACT_APP_BACKEND_URL || ""}${hisStory.feature_photo_url}`
                        }
                        alt="Feature photo preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center px-3">
                        <div className="text-4xl mb-1" aria-hidden>📷</div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#6D5A50]">
                          coming soon
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label
                      className="btn-pasture inline-flex items-center gap-2 cursor-pointer disabled:opacity-60"
                      style={{ pointerEvents: uploadingHisStoryPhoto ? "none" : "auto" }}
                    >
                      <ImageIcon size={14} />
                      {uploadingHisStoryPhoto
                        ? "Uploading…"
                        : hisStory.feature_photo_url
                          ? "Replace photo"
                          : "Upload photo"}
                      <input
                        data-testid="admin-his-story-photo-input"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          uploadHisStoryPhoto(f);
                        }}
                      />
                    </label>
                    {hisStory.feature_photo_url && (
                      <button
                        data-testid="admin-his-story-photo-clear"
                        type="button"
                        onClick={clearHisStoryPhoto}
                        className="text-xs uppercase tracking-[0.25em] text-[#A87149] hover:text-[#4B3A2F] underline-offset-4 hover:underline transition-colors"
                      >
                        remove photo (show placeholder)
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Photo caption */}
              <div>
                <label className={labelCls}>
                  Photo caption{" "}
                  <span className="normal-case tracking-normal text-[#6D5A50]">
                    (the script line under the Polaroid — also shown inside the placeholder while &ldquo;coming soon&rdquo;)
                  </span>
                </label>
                <input
                  data-testid="admin-his-story-photo-caption"
                  className={inputCls}
                  value={hisStory.feature_photo_caption || ""}
                  onChange={(e) =>
                    setHisStory({
                      ...hisStory,
                      feature_photo_caption: e.target.value,
                    })
                  }
                />
              </div>

              <button
                data-testid="admin-his-story-save-btn"
                type="submit"
                disabled={savingHisStory}
                className="btn-pasture mt-2 disabled:opacity-60"
              >
                {savingHisStory ? "Saving…" : "Save His Story, Our Yes"}
              </button>
            </div>
          )}
        </form>

        {/* Lil Salties — page content (every hardcoded string) */}
        <form
          onSubmit={saveLsp}
          data-testid="admin-lil-salties-page-form"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mb-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            Lil Salties™ — Page Content
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            every word on the public Lil Salties page — edit freely
          </p>

          {!lsp ? (
            <p className="text-sm text-[#6D5A50] italic">loading page content&hellip;</p>
          ) : (
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">SEO</p>
                <div>
                  <label className={labelCls}>SEO title</label>
                  <input
                    data-testid="admin-lsp-seo-title"
                    className={inputCls}
                    value={lsp.seo_title || ""}
                    onChange={(e) => setLsp({ ...lsp, seo_title: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>SEO description</label>
                  <textarea
                    data-testid="admin-lsp-seo-description"
                    rows={2}
                    className={inputCls}
                    value={lsp.seo_description || ""}
                    onChange={(e) => setLsp({ ...lsp, seo_description: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Hero{" "}
                  <span className="normal-case tracking-normal text-[#6D5A50]">
                    (rainbow palette is applied automatically to each word)
                  </span>
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Eyebrow (small line above title)</label>
                    <input
                      data-testid="admin-lsp-hero-eyebrow"
                      className={inputCls}
                      value={lsp.hero_eyebrow || ""}
                      onChange={(e) => setLsp({ ...lsp, hero_eyebrow: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Hero title</label>
                    <input
                      data-testid="admin-lsp-hero-title"
                      className={inputCls}
                      value={lsp.hero_title || ""}
                      onChange={(e) => setLsp({ ...lsp, hero_title: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Hero subtitle</label>
                  <input
                    data-testid="admin-lsp-hero-subtitle"
                    className={inputCls}
                    value={lsp.hero_subtitle || ""}
                    onChange={(e) => setLsp({ ...lsp, hero_subtitle: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Welcome card
                </p>
                <div>
                  <label className={labelCls}>Subtitle question (red italic line)</label>
                  <input
                    data-testid="admin-lsp-card-subtitle"
                    className={inputCls}
                    value={lsp.card_subtitle_question || ""}
                    onChange={(e) => setLsp({ ...lsp, card_subtitle_question: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Body{" "}
                    <span className="normal-case tracking-normal text-[#6D5A50]">
                      (bold, italic, links, bullets)
                    </span>
                  </label>
                  <div
                    data-testid="admin-lsp-body-wysiwyg"
                    className="rounded-md border border-[#E7DFD5] bg-[#FBF7F0] overflow-hidden focus-within:border-[#B08A5A]"
                  >
                    <EditorProvider>
                      <Editor
                        value={lsp.body_html || ""}
                        onChange={(e) => setLsp({ ...lsp, body_html: e.target.value })}
                        containerProps={{
                          style: {
                            background: "#FBF7F0",
                            border: "none",
                            minHeight: "280px",
                            fontFamily: "Georgia, 'Times New Roman', serif",
                            fontSize: "15px",
                            lineHeight: "1.7",
                            color: "#2C2724",
                          },
                        }}
                      >
                        <Toolbar style={{ background: "#F3EBDD", borderBottom: "1px solid #E7DFD5" }}>
                          <BtnBold /><BtnItalic /><BtnUnderline />
                          <Separator />
                          <BtnBulletList /><BtnNumberedList />
                          <Separator />
                          <BtnLink /><BtnClearFormatting />
                        </Toolbar>
                      </Editor>
                    </EditorProvider>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Next-page button
                </p>
                <div>
                  <label className={labelCls}>Button label</label>
                  <input
                    data-testid="admin-lsp-next-button"
                    className={inputCls}
                    value={lsp.next_button_label || ""}
                    onChange={(e) => setLsp({ ...lsp, next_button_label: e.target.value })}
                  />
                </div>
              </div>

              <button
                data-testid="admin-lsp-save-btn"
                type="submit"
                disabled={savingLsp}
                className="btn-pasture mt-2 disabled:opacity-60"
              >
                {savingLsp ? "Saving…" : "Save Lil Salties page"}
              </button>
            </div>
          )}
        </form>

        {/* Meet the Herd — page content */}
        <form
          onSubmit={saveMthp}
          data-testid="admin-meet-the-herd-page-form"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mb-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            Meet the Herd — Page Content
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            every word on the public Meet the Herd page — edit freely
          </p>

          {!mthp ? (
            <p className="text-sm text-[#6D5A50] italic">loading page content&hellip;</p>
          ) : (
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">SEO</p>
                <div>
                  <label className={labelCls}>SEO title</label>
                  <input
                    data-testid="admin-mthp-seo-title"
                    className={inputCls}
                    value={mthp.seo_title || ""}
                    onChange={(e) => setMthp({ ...mthp, seo_title: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>SEO description</label>
                  <textarea
                    data-testid="admin-mthp-seo-description"
                    rows={2}
                    className={inputCls}
                    value={mthp.seo_description || ""}
                    onChange={(e) => setMthp({ ...mthp, seo_description: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">Hero</p>
                <div>
                  <label className={labelCls}>Hero subtitle</label>
                  <input
                    data-testid="admin-mthp-hero-subtitle"
                    className={inputCls}
                    value={mthp.hero_subtitle || ""}
                    onChange={(e) => setMthp({ ...mthp, hero_subtitle: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Scripture card
                </p>
                <div>
                  <label className={labelCls}>
                    Scripture{" "}
                    <span className="normal-case tracking-normal text-[#6D5A50]">
                      (rendered as an italic blockquote — quotation marks added automatically)
                    </span>
                  </label>
                  <textarea
                    data-testid="admin-mthp-scripture-text"
                    rows={3}
                    className={inputCls}
                    value={mthp.scripture_text || ""}
                    onChange={(e) => setMthp({ ...mthp, scripture_text: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>Scripture reference</label>
                  <input
                    data-testid="admin-mthp-scripture-reference"
                    className={inputCls}
                    value={mthp.scripture_reference || ""}
                    onChange={(e) => setMthp({ ...mthp, scripture_reference: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Body{" "}
                    <span className="normal-case tracking-normal text-[#6D5A50]">
                      (bold, italic, links, bullets)
                    </span>
                  </label>
                  <div
                    data-testid="admin-mthp-body-wysiwyg"
                    className="rounded-md border border-[#E7DFD5] bg-[#FBF7F0] overflow-hidden focus-within:border-[#B08A5A]"
                  >
                    <EditorProvider>
                      <Editor
                        value={mthp.body_html || ""}
                        onChange={(e) => setMthp({ ...mthp, body_html: e.target.value })}
                        containerProps={{
                          style: {
                            background: "#FBF7F0",
                            border: "none",
                            minHeight: "220px",
                            fontFamily: "Georgia, 'Times New Roman', serif",
                            fontSize: "15px",
                            lineHeight: "1.7",
                            color: "#2C2724",
                          },
                        }}
                      >
                        <Toolbar style={{ background: "#F3EBDD", borderBottom: "1px solid #E7DFD5" }}>
                          <BtnBold /><BtnItalic /><BtnUnderline />
                          <Separator />
                          <BtnBulletList /><BtnNumberedList />
                          <Separator />
                          <BtnLink /><BtnClearFormatting />
                        </Toolbar>
                      </Editor>
                    </EditorProvider>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Closing script line</label>
                  <input
                    data-testid="admin-mthp-closing-line"
                    className={inputCls}
                    value={mthp.closing_script_line || ""}
                    onChange={(e) => setMthp({ ...mthp, closing_script_line: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  More Than a Herd — companion sayings
                </p>
                <div>
                  <label className={labelCls}>Section heading</label>
                  <input
                    data-testid="admin-mthp-more-heading"
                    className={inputCls}
                    value={mthp.more_than_heading || ""}
                    onChange={(e) => setMthp({ ...mthp, more_than_heading: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>Intro sentence (above the two cards)</label>
                  <textarea
                    data-testid="admin-mthp-more-intro"
                    rows={2}
                    className={inputCls}
                    value={mthp.more_than_intro_body || ""}
                    onChange={(e) => setMthp({ ...mthp, more_than_intro_body: e.target.value })}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4 p-4 rounded-md bg-[#F9F1E1] border border-[#C8A876]/40">
                  <div className="sm:col-span-2 text-[10px] uppercase tracking-[0.3em] text-[#A87149]">
                    Bray card
                  </div>
                  <div>
                    <label className={labelCls}>Saying</label>
                    <input
                      data-testid="admin-mthp-more-saying-bray"
                      className={inputCls}
                      value={mthp.more_than_saying_bray || ""}
                      onChange={(e) => setMthp({ ...mthp, more_than_saying_bray: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Link label</label>
                    <input
                      data-testid="admin-mthp-more-bray-link"
                      className={inputCls}
                      value={mthp.more_than_bray_link_label || ""}
                      onChange={(e) => setMthp({ ...mthp, more_than_bray_link_label: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Sub-line</label>
                    <input
                      data-testid="admin-mthp-more-donkey-line"
                      className={inputCls}
                      value={mthp.more_than_donkey_line || ""}
                      onChange={(e) => setMthp({ ...mthp, more_than_donkey_line: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 p-4 rounded-md bg-[#F9F1E1] border border-[#7E9268]/40">
                  <div className="sm:col-span-2 text-[10px] uppercase tracking-[0.3em] text-[#7E9268]">
                    Moo card
                  </div>
                  <div>
                    <label className={labelCls}>Saying</label>
                    <input
                      data-testid="admin-mthp-more-saying-moo"
                      className={inputCls}
                      value={mthp.more_than_saying_moo || ""}
                      onChange={(e) => setMthp({ ...mthp, more_than_saying_moo: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Link label</label>
                    <input
                      data-testid="admin-mthp-more-moo-link"
                      className={inputCls}
                      value={mthp.more_than_moo_link_label || ""}
                      onChange={(e) => setMthp({ ...mthp, more_than_moo_link_label: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Sub-line</label>
                    <input
                      data-testid="admin-mthp-more-cow-line"
                      className={inputCls}
                      value={mthp.more_than_cow_line || ""}
                      onChange={(e) => setMthp({ ...mthp, more_than_cow_line: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Closing sentence (below the two cards)</label>
                  <textarea
                    data-testid="admin-mthp-more-outro"
                    rows={2}
                    className={inputCls}
                    value={mthp.more_than_outro_body || ""}
                    onChange={(e) => setMthp({ ...mthp, more_than_outro_body: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Next-page button
                </p>
                <div>
                  <label className={labelCls}>Button label</label>
                  <input
                    data-testid="admin-mthp-next-button"
                    className={inputCls}
                    value={mthp.next_button_label || ""}
                    onChange={(e) => setMthp({ ...mthp, next_button_label: e.target.value })}
                  />
                </div>
              </div>

              <button
                data-testid="admin-mthp-save-btn"
                type="submit"
                disabled={savingMthp}
                className="btn-pasture mt-2 disabled:opacity-60"
              >
                {savingMthp ? "Saving…" : "Save Meet the Herd page"}
              </button>
            </div>
          )}
        </form>

        {/* Saltie Brays — page content */}
        <form
          onSubmit={saveSbp}
          data-testid="admin-saltie-brays-page-form"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mb-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">Saltie Brays™ — Page Content</h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">every word on the public Saltie Brays page</p>
          {!sbp ? (
            <p className="text-sm text-[#6D5A50] italic">loading page content&hellip;</p>
          ) : (
            <div className="space-y-5">
              <div>
                <label className={labelCls}>SEO title</label>
                <input data-testid="admin-sbp-seo-title" className={inputCls} value={sbp.seo_title || ""} onChange={(e) => setSbp({ ...sbp, seo_title: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>SEO description</label>
                <textarea data-testid="admin-sbp-seo-description" rows={2} className={inputCls} value={sbp.seo_description || ""} onChange={(e) => setSbp({ ...sbp, seo_description: e.target.value })} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Hero title</label>
                  <input data-testid="admin-sbp-hero-title" className={inputCls} value={sbp.hero_title || ""} onChange={(e) => setSbp({ ...sbp, hero_title: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Hero subtitle</label>
                  <input data-testid="admin-sbp-hero-subtitle" className={inputCls} value={sbp.hero_subtitle || ""} onChange={(e) => setSbp({ ...sbp, hero_subtitle: e.target.value })} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Intro paragraph (above bio grid)</label>
                <textarea data-testid="admin-sbp-intro-body" rows={3} className={inputCls} value={sbp.intro_body || ""} onChange={(e) => setSbp({ ...sbp, intro_body: e.target.value })} />
              </div>

              <div className="space-y-4 pt-4 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Hear a Bray. Pray. — companion feature (below bios)
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Heading</label>
                    <input data-testid="admin-sbp-hear-heading" className={inputCls} value={sbp.hear_a_bray_heading || ""} onChange={(e) => setSbp({ ...sbp, hear_a_bray_heading: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls}>Closing line (below body)</label>
                    <input data-testid="admin-sbp-hear-closing" className={inputCls} value={sbp.hear_a_bray_closing || ""} onChange={(e) => setSbp({ ...sbp, hear_a_bray_closing: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Subheading (script line)</label>
                  <input data-testid="admin-sbp-hear-subheading" className={inputCls} value={sbp.hear_a_bray_subheading || ""} onChange={(e) => setSbp({ ...sbp, hear_a_bray_subheading: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Body <span className="normal-case tracking-normal text-[#6D5A50]">(bold, italic, links, bullets)</span></label>
                  <div data-testid="admin-sbp-hear-body-wysiwyg" className="rounded-md border border-[#E7DFD5] bg-[#FBF7F0] overflow-hidden focus-within:border-[#B08A5A]">
                    <EditorProvider>
                      <Editor
                        value={sbp.hear_a_bray_body_html || ""}
                        onChange={(e) => setSbp({ ...sbp, hear_a_bray_body_html: e.target.value })}
                        containerProps={{ style: { background: "#FBF7F0", border: "none", minHeight: "260px", fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: "1.7", color: "#2C2724" } }}
                      >
                        <Toolbar style={{ background: "#F3EBDD", borderBottom: "1px solid #E7DFD5" }}>
                          <BtnBold /><BtnItalic /><BtnUnderline /><Separator /><BtnBulletList /><BtnNumberedList /><Separator /><BtnLink /><BtnClearFormatting />
                        </Toolbar>
                      </Editor>
                    </EditorProvider>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Photo alt text (screen-reader description)</label>
                  <input data-testid="admin-sbp-hear-photo-alt" className={inputCls} value={sbp.hear_a_bray_photo_alt || ""} onChange={(e) => setSbp({ ...sbp, hear_a_bray_photo_alt: e.target.value })} />
                </div>
                <div className="grid sm:grid-cols-[1fr,auto] gap-4 items-end">
                  <div>
                    <label className={labelCls}>Bonnie photo</label>
                    <p className="text-xs text-[#6D5A50] mb-2 leading-relaxed">
                      Portrait or landscape — original proportions are preserved (never cropped or stretched).
                      Max 12&nbsp;MB. Re-uploading replaces the current photo.
                    </p>
                    {sbp.hear_a_bray_photo_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL || ""}${sbp.hear_a_bray_photo_url}`}
                        alt=""
                        className="max-w-xs max-h-40 rounded border border-[#E7DFD5]"
                      />
                    ) : (
                      <p className="text-xs italic text-[#6D5A50]">no photo uploaded yet</p>
                    )}
                  </div>
                  <label
                    data-testid="admin-sbp-hear-photo-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#4B3A2F] text-white font-body text-sm cursor-pointer hover:bg-[#2C2724]"
                  >
                    {sbp.hear_a_bray_photo_url ? "Replace photo" : "Upload photo"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        try {
                          const res = await uploadHearABrayPhoto(f, passcode);
                          setSbp({ ...sbp, hear_a_bray_photo_url: res.url });
                          toast("Bonnie photo uploaded.");
                        } catch {
                          toast("Couldn't upload the photo.");
                        }
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>

              <div>
                <label className={labelCls}>Next-page button label</label>
                <input data-testid="admin-sbp-next-button" className={inputCls} value={sbp.next_button_label || ""} onChange={(e) => setSbp({ ...sbp, next_button_label: e.target.value })} />
              </div>
              <button data-testid="admin-sbp-save-btn" type="submit" disabled={savingSbp} className="btn-pasture mt-2 disabled:opacity-60">
                {savingSbp ? "Saving…" : "Save Saltie Brays page"}
              </button>
            </div>
          )}
        </form>

        {/* Saltie Moos — page content */}
        <form
          onSubmit={saveSmp}
          data-testid="admin-saltie-moos-page-form"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mb-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">Saltie Moos™ — Page Content</h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">every word on the public Saltie Moos page</p>
          {!smp ? (
            <p className="text-sm text-[#6D5A50] italic">loading page content&hellip;</p>
          ) : (
            <div className="space-y-5">
              <div>
                <label className={labelCls}>SEO title</label>
                <input data-testid="admin-smp-seo-title" className={inputCls} value={smp.seo_title || ""} onChange={(e) => setSmp({ ...smp, seo_title: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>SEO description</label>
                <textarea data-testid="admin-smp-seo-description" rows={2} className={inputCls} value={smp.seo_description || ""} onChange={(e) => setSmp({ ...smp, seo_description: e.target.value })} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Hero title</label>
                  <input data-testid="admin-smp-hero-title" className={inputCls} value={smp.hero_title || ""} onChange={(e) => setSmp({ ...smp, hero_title: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Hero subtitle</label>
                  <input data-testid="admin-smp-hero-subtitle" className={inputCls} value={smp.hero_subtitle || ""} onChange={(e) => setSmp({ ...smp, hero_subtitle: e.target.value })} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Intro paragraph (above bio grid)</label>
                <textarea data-testid="admin-smp-intro-body" rows={3} className={inputCls} value={smp.intro_body || ""} onChange={(e) => setSmp({ ...smp, intro_body: e.target.value })} />
              </div>

              <div className="space-y-4 pt-4 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Hear a Moo. Praise, Too. — companion feature (below bios)
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Heading</label>
                    <input data-testid="admin-smp-hear-heading" className={inputCls} value={smp.hear_a_moo_heading || ""} onChange={(e) => setSmp({ ...smp, hear_a_moo_heading: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls}>Closing line (below body)</label>
                    <input data-testid="admin-smp-hear-closing" className={inputCls} value={smp.hear_a_moo_closing || ""} onChange={(e) => setSmp({ ...smp, hear_a_moo_closing: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Subheading (script line)</label>
                  <input data-testid="admin-smp-hear-subheading" className={inputCls} value={smp.hear_a_moo_subheading || ""} onChange={(e) => setSmp({ ...smp, hear_a_moo_subheading: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Body <span className="normal-case tracking-normal text-[#6D5A50]">(bold, italic, links, bullets)</span></label>
                  <div data-testid="admin-smp-hear-body-wysiwyg" className="rounded-md border border-[#E7DFD5] bg-[#FBF7F0] overflow-hidden focus-within:border-[#B08A5A]">
                    <EditorProvider>
                      <Editor
                        value={smp.hear_a_moo_body_html || ""}
                        onChange={(e) => setSmp({ ...smp, hear_a_moo_body_html: e.target.value })}
                        containerProps={{ style: { background: "#FBF7F0", border: "none", minHeight: "260px", fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: "1.7", color: "#2C2724" } }}
                      >
                        <Toolbar style={{ background: "#F3EBDD", borderBottom: "1px solid #E7DFD5" }}>
                          <BtnBold /><BtnItalic /><BtnUnderline /><Separator /><BtnBulletList /><BtnNumberedList /><Separator /><BtnLink /><BtnClearFormatting />
                        </Toolbar>
                      </Editor>
                    </EditorProvider>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Photo alt text (screen-reader description)</label>
                  <input data-testid="admin-smp-hear-photo-alt" className={inputCls} value={smp.hear_a_moo_photo_alt || ""} onChange={(e) => setSmp({ ...smp, hear_a_moo_photo_alt: e.target.value })} />
                </div>
                <div className="grid sm:grid-cols-[1fr,auto] gap-4 items-end">
                  <div>
                    <label className={labelCls}>Cow photo</label>
                    <p className="text-xs text-[#6D5A50] mb-2 leading-relaxed">
                      Portrait or landscape — original proportions are preserved (never cropped or stretched).
                      Max 12&nbsp;MB. Re-uploading replaces the current photo.
                    </p>
                    {smp.hear_a_moo_photo_url ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL || ""}${smp.hear_a_moo_photo_url}`}
                        alt=""
                        className="max-w-xs max-h-40 rounded border border-[#E7DFD5]"
                      />
                    ) : (
                      <p className="text-xs italic text-[#6D5A50]">no photo uploaded yet</p>
                    )}
                  </div>
                  <label
                    data-testid="admin-smp-hear-photo-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#4B3A2F] text-white font-body text-sm cursor-pointer hover:bg-[#2C2724]"
                  >
                    {smp.hear_a_moo_photo_url ? "Replace photo" : "Upload photo"}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        try {
                          const res = await uploadHearAMooPhoto(f, passcode);
                          setSmp({ ...smp, hear_a_moo_photo_url: res.url });
                          toast("Cow photo uploaded.");
                        } catch {
                          toast("Couldn't upload the photo.");
                        }
                        e.target.value = "";
                      }}
                    />
                  </label>
                </div>
              </div>

              <div>
                <label className={labelCls}>Next-page button label</label>
                <input data-testid="admin-smp-next-button" className={inputCls} value={smp.next_button_label || ""} onChange={(e) => setSmp({ ...smp, next_button_label: e.target.value })} />
              </div>
              <button data-testid="admin-smp-save-btn" type="submit" disabled={savingSmp} className="btn-pasture mt-2 disabled:opacity-60">
                {savingSmp ? "Saving…" : "Save Saltie Moos page"}
              </button>
            </div>
          )}
        </form>

        {/* Front Porch — page content */}
        <form
          onSubmit={saveFpp}
          data-testid="admin-front-porch-page-form"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mb-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">Front Porch Swing — Page Content</h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">every word on the public Front Porch page</p>
          {!fpp ? (
            <p className="text-sm text-[#6D5A50] italic">loading page content&hellip;</p>
          ) : (
            <div className="space-y-6">
              <div className="space-y-4">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">SEO</p>
                <div>
                  <label className={labelCls}>SEO title</label>
                  <input data-testid="admin-fpp-seo-title" className={inputCls} value={fpp.seo_title || ""} onChange={(e) => setFpp({ ...fpp, seo_title: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>SEO description</label>
                  <textarea data-testid="admin-fpp-seo-description" rows={2} className={inputCls} value={fpp.seo_description || ""} onChange={(e) => setFpp({ ...fpp, seo_description: e.target.value })} />
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">Hero</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Hero title</label>
                    <input data-testid="admin-fpp-hero-title" className={inputCls} value={fpp.hero_title || ""} onChange={(e) => setFpp({ ...fpp, hero_title: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls}>Hero subtitle</label>
                    <input data-testid="admin-fpp-hero-subtitle" className={inputCls} value={fpp.hero_subtitle || ""} onChange={(e) => setFpp({ ...fpp, hero_subtitle: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">Porch photo caption + intro</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Photo caption — name line</label>
                    <input data-testid="admin-fpp-photo-name" className={inputCls} value={fpp.photo_caption_name || ""} onChange={(e) => setFpp({ ...fpp, photo_caption_name: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls}>Photo caption — subtitle (small caps)</label>
                    <input data-testid="admin-fpp-photo-subtitle" className={inputCls} value={fpp.photo_caption_subtitle || ""} onChange={(e) => setFpp({ ...fpp, photo_caption_subtitle: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Intro body (italic display)</label>
                  <div data-testid="admin-fpp-intro-body-wysiwyg" className="rounded-md border border-[#E7DFD5] bg-[#FBF7F0] overflow-hidden focus-within:border-[#B08A5A]">
                    <EditorProvider>
                      <Editor value={fpp.intro_body_html || ""} onChange={(e) => setFpp({ ...fpp, intro_body_html: e.target.value })} containerProps={{ style: { background: "#FBF7F0", border: "none", minHeight: "160px", fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: "1.7", color: "#2C2724" } }}>
                        <Toolbar style={{ background: "#F3EBDD", borderBottom: "1px solid #E7DFD5" }}>
                          <BtnBold /><BtnItalic /><BtnUnderline /><Separator /><BtnBulletList /><BtnNumberedList /><Separator /><BtnLink /><BtnClearFormatting />
                        </Toolbar>
                      </Editor>
                    </EditorProvider>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">Peek-a-Moo explainer</p>
                <div>
                  <label className={labelCls}>Heading (script line)</label>
                  <input data-testid="admin-fpp-peek-heading" className={inputCls} value={fpp.peek_a_moo_heading || ""} onChange={(e) => setFpp({ ...fpp, peek_a_moo_heading: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Body</label>
                  <div data-testid="admin-fpp-peek-body-wysiwyg" className="rounded-md border border-[#E7DFD5] bg-[#FBF7F0] overflow-hidden focus-within:border-[#B08A5A]">
                    <EditorProvider>
                      <Editor value={fpp.peek_a_moo_body_html || ""} onChange={(e) => setFpp({ ...fpp, peek_a_moo_body_html: e.target.value })} containerProps={{ style: { background: "#FBF7F0", border: "none", minHeight: "150px", fontFamily: "Georgia, serif", fontSize: "15px", lineHeight: "1.7", color: "#2C2724" } }}>
                        <Toolbar style={{ background: "#F3EBDD", borderBottom: "1px solid #E7DFD5" }}>
                          <BtnBold /><BtnItalic /><BtnUnderline /><Separator /><BtnBulletList /><BtnNumberedList /><Separator /><BtnLink /><BtnClearFormatting />
                        </Toolbar>
                      </Editor>
                    </EditorProvider>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">Newsletter card</p>
                <div>
                  <label className={labelCls}>Heading</label>
                  <input data-testid="admin-fpp-news-heading" className={inputCls} value={fpp.newsletter_heading || ""} onChange={(e) => setFpp({ ...fpp, newsletter_heading: e.target.value })} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Script line 1</label>
                    <input data-testid="admin-fpp-news-line1" className={inputCls} value={fpp.newsletter_script_line_1 || ""} onChange={(e) => setFpp({ ...fpp, newsletter_script_line_1: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls}>Script line 2</label>
                    <input data-testid="admin-fpp-news-line2" className={inputCls} value={fpp.newsletter_script_line_2 || ""} onChange={(e) => setFpp({ ...fpp, newsletter_script_line_2: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Body paragraph</label>
                  <textarea data-testid="admin-fpp-news-body" rows={3} className={inputCls} value={fpp.newsletter_body || ""} onChange={(e) => setFpp({ ...fpp, newsletter_body: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Substack footer note (under the embed)</label>
                  <input data-testid="admin-fpp-substack-footer" className={inputCls} value={fpp.substack_footer_note || ""} onChange={(e) => setFpp({ ...fpp, substack_footer_note: e.target.value })} />
                </div>
              </div>

              <div className="pt-4 border-t border-[#E7DFD5]">
                <label className={labelCls}>Next-page button label</label>
                <input data-testid="admin-fpp-next-button" className={inputCls} value={fpp.next_button_label || ""} onChange={(e) => setFpp({ ...fpp, next_button_label: e.target.value })} />
              </div>

              <button data-testid="admin-fpp-save-btn" type="submit" disabled={savingFpp} className="btn-pasture mt-2 disabled:opacity-60">
                {savingFpp ? "Saving…" : "Save Front Porch page"}
              </button>
            </div>
          )}
        </form>

        {/* ─────────────────────────────────────────────────────────
           Write a Saltie Shout-Out — a writing desk, not a CMS.
           Every field label is deliberate ("Recipient", "My Letter",
           "Publish my Saltie Shout-Out") to reinforce that this is
           the moment of sitting down to write someone a note, not
           editing a database record.
        ─────────────────────────────────────────────────────────── */}
        <form
          onSubmit={publishSso}
          data-testid="admin-saltie-shout-out-form"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mb-12"
        >
          <p className="text-xs uppercase tracking-[0.34em] text-[#7E9268] mb-3">
            At the writing desk
          </p>
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            Write a Saltie Shout-Out<sup className="align-super">™</sup>
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            for someone who has quietly made a difference
          </p>

          {!sso ? (
            <p className="text-sm text-[#6D5A50] italic">
              opening today&rsquo;s letter&hellip;
            </p>
          ) : (
            <div className="space-y-6">
              {/* Recipient */}
              <div>
                <label className={labelCls} htmlFor="sso-recipient">
                  Recipient
                </label>
                <input
                  id="sso-recipient"
                  data-testid="admin-sso-recipient"
                  className={inputCls}
                  placeholder="Their name (e.g. Emma, Grandma Ruth, the crew at church)"
                  value={sso.recipient || ""}
                  onChange={(e) => setSso({ ...sso, recipient: e.target.value })}
                />
                <p className="text-xs text-[#7B6B60] mt-1">
                  Who this Saltie Shout-Out is for. Kept on record even
                  though the letter now flows straight into the message.
                </p>
              </div>

              {/* Optional keepsake photo */}
              <div>
                <label className={labelCls}>
                  Photo <span className="text-[#7B6B60]/70 normal-case tracking-normal">(optional keepsake)</span>
                </label>
                <div className="flex items-center gap-4 mt-1">
                  <div
                    className="w-24 h-24 rounded-[3px] border border-[#D8CCB8] bg-[#F9F1DE] overflow-hidden flex items-center justify-center"
                    data-testid="admin-sso-photo-preview"
                  >
                    {sso.photo_url ? (
                      <img
                        src={sso.photo_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Camera size={22} className="text-[#B0A28E]" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label
                      htmlFor="sso-photo-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#B08A5A] text-[#4B3A2F] bg-[#F9F1DE] hover:bg-[#F3E7CC] cursor-pointer text-sm"
                    >
                      <ImageIcon size={14} />
                      {uploadingSsoPhoto ? "Tucking in…" : sso.photo_url ? "Replace photo" : "Add a keepsake photo"}
                    </label>
                    <input
                      id="sso-photo-upload"
                      data-testid="admin-sso-photo-upload"
                      type="file"
                      accept="image/*"
                      onChange={onSsoPhotoChange}
                      disabled={uploadingSsoPhoto}
                      className="hidden"
                    />
                    {sso.photo_url ? (
                      <button
                        type="button"
                        onClick={clearSsoPhoto}
                        data-testid="admin-sso-photo-clear"
                        className="ml-3 text-xs text-[#7B6B60] underline hover:text-[#4B3A2F]"
                      >
                        remove photo
                      </button>
                    ) : null}
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.24em] text-[#7B6B60]">
                        Photo caption (for screen readers)
                      </label>
                      <input
                        data-testid="admin-sso-photo-alt"
                        className={inputCls}
                        placeholder="e.g. Emma smiling on her porch"
                        value={sso.photo_alt || ""}
                        onChange={(e) => setSso({ ...sso, photo_alt: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Letter body */}
              <div>
                <label className={labelCls} htmlFor="sso-body">
                  My letter
                </label>
                <textarea
                  id="sso-body"
                  data-testid="admin-sso-body"
                  rows={10}
                  className={inputCls}
                  placeholder="Write from the heart. Blank lines separate paragraphs."
                  value={sso.body || ""}
                  onChange={(e) => setSso({ ...sso, body: e.target.value })}
                  style={{ fontFamily: "Georgia, serif", lineHeight: 1.7 }}
                />
                <p className="text-xs text-[#7B6B60] mt-1">
                  The closing (&ldquo;With Love, / Stefanie / The Salty Moo&rdquo;)
                  is added automatically &mdash; just write the message.
                </p>
              </div>

              {/* Closing date */}
              <div>
                <label className={labelCls} htmlFor="sso-date">
                  Closing date <span className="text-[#7B6B60]/70 normal-case tracking-normal">(optional)</span>
                </label>
                <input
                  id="sso-date"
                  data-testid="admin-sso-closing-date"
                  className={inputCls}
                  placeholder='e.g. "October 2025" or "Autumn on the pasture"'
                  value={sso.closing_date || ""}
                  onChange={(e) => setSso({ ...sso, closing_date: e.target.value })}
                />
                <p className="text-xs text-[#7B6B60] mt-1">
                  Renders as a small caps eyebrow at the top of the letter.
                </p>
              </div>

              {/* Action row: preview + publish */}
              <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#E7DFD5]">
                <button
                  type="button"
                  onClick={() => setPreviewSso((p) => !p)}
                  data-testid="admin-sso-preview-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-md border border-[#B08A5A] text-[#4B3A2F] bg-[#F9F1DE] hover:bg-[#F3E7CC] text-sm"
                >
                  <BookOpen size={14} />
                  {previewSso ? "Hide preview" : "Preview my letter"}
                </button>
                <button
                  type="submit"
                  disabled={savingSso}
                  data-testid="admin-sso-publish-btn"
                  className="btn-pasture disabled:opacity-60"
                >
                  {savingSso ? "Publishing…" : "Publish my Saltie Shout-Out"}
                </button>
                <button
                  type="button"
                  onClick={archiveAndRestart}
                  disabled={archivingSso}
                  data-testid="admin-sso-archive-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#7E9268] text-[#4B3A2F] bg-transparent hover:bg-[#EEE9DA] text-sm disabled:opacity-60"
                  title="Tucks the current letter into the keepsake book and clears the writing desk for a fresh letter."
                >
                  {archivingSso ? "Tucking in…" : "Archive & start a new letter"}
                </button>
                {sso.updated_at ? (
                  <span className="text-xs text-[#7B6B60] ml-auto">
                    last written {new Date(sso.updated_at).toLocaleString()}
                  </span>
                ) : null}
              </div>

              {/* Live preview — renders the exact stationery component
                  the public page uses so Stefanie sees the letter as
                  the reader will see it. */}
              {previewSso ? (
                <div
                  data-testid="admin-sso-preview"
                  className="mt-6 p-6 sm:p-10 rounded-md"
                  style={{
                    background:
                      "radial-gradient(ellipse at 50% 0%, #2b2723 0%, #1a1614 100%)",
                  }}
                >
                  <p className="text-center text-[10px] uppercase tracking-[0.34em] text-[#B08A5A] mb-6">
                    preview — how the reader will see it
                  </p>
                  <SaltieShoutOutStationery
                    recipient={sso.recipient}
                    body={sso.body}
                    photoUrl={sso.photo_url || null}
                    photoAlt={sso.photo_alt || ""}
                    closingDate={sso.closing_date || ""}
                  />
                </div>
              ) : null}

              {/* The keepsake book — compact list of every past letter
                  tucked into the archive. Lets Stefanie see her writing
                  history, revisit a letter, or (rarely) remove one. */}
              {ssoArchive.length > 0 ? (
                <div
                  className="mt-8 pt-6 border-t border-[#E7DFD5]"
                  data-testid="admin-sso-archive-list"
                >
                  <p className="text-xs uppercase tracking-[0.34em] text-[#7E9268] mb-1">
                    The keepsake book
                  </p>
                  <h3 className="font-display text-xl text-[#4B3A2F] mb-3">
                    Past letters ({ssoArchive.length})
                  </h3>
                  <ul className="divide-y divide-[#EFE7D7]">
                    {ssoArchive.map((letter) => (
                      <li
                        key={letter.id}
                        data-testid={`admin-sso-archive-item-${letter.id}`}
                        className="py-3 flex items-start gap-4"
                      >
                        <div className="flex-1 min-w-0">
                          <p
                            className="font-display text-[#3B2E24]"
                            style={{ fontSize: "1.05rem", lineHeight: 1.3 }}
                          >
                            To {letter.recipient}
                          </p>
                          <p className="text-xs text-[#7B6B60] mt-0.5">
                            {letter.closing_date
                              ? `${letter.closing_date} · `
                              : ""}
                            archived{" "}
                            {new Date(letter.published_at).toLocaleDateString()}
                          </p>
                          <p
                            className="text-sm text-[#4B3A2F]/80 mt-1"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {(letter.body || "").replace(/\s+/g, " ").trim()}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteArchivedSso(letter)}
                          data-testid={`admin-sso-archive-delete-${letter.id}`}
                          className="text-xs text-[#B04A38] hover:text-[#8A2E1F] underline shrink-0 mt-1"
                        >
                          remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          )}
        </form>

        {/* Story Barn Shop — merchandise, orders, fulfillment */}
        <section
          data-testid="admin-shop-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mb-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            Story Barn Shop
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            merchandise, orders &amp; shipping
          </p>
          <AdminShop passcode={passcode} />
        </section>

        {/* Story Barn editor */}
        <section
          data-testid="admin-story-barn-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            Story Barn &amp; Mercantile
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            what's on the shelves
          </p>

          {/* Add new item */}
          <form
            onSubmit={addBarnItem}
            data-testid="admin-barn-add-form"
            className="mb-8 p-5 rounded-md bg-[#F9F6F0] border border-dashed border-[#A87149]"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-4 flex items-center gap-2">
              <Plus size={14} /> Add a new product
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <input
                data-testid="admin-barn-new-slug"
                className={inputCls}
                placeholder="slug (e.g. coffee-mug)"
                value={newItem.slug}
                onChange={(e) => setNewItem({ ...newItem, slug: e.target.value })}
              />
              <input
                data-testid="admin-barn-new-title"
                className={inputCls}
                placeholder="title"
                value={newItem.title}
                onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
              />
              <input
                data-testid="admin-barn-new-kind"
                className={inputCls}
                placeholder="kind (Picture Book, Audio, Mercantile…)"
                value={newItem.kind}
                onChange={(e) => setNewItem({ ...newItem, kind: e.target.value })}
              />
              <input
                data-testid="admin-barn-new-order"
                type="number"
                className={inputCls}
                placeholder="order (lower = earlier)"
                value={newItem.order}
                onChange={(e) => setNewItem({ ...newItem, order: e.target.value })}
              />
              <textarea
                data-testid="admin-barn-new-note"
                rows={2}
                className={`${inputCls} sm:col-span-2`}
                placeholder="short note shown on the card"
                value={newItem.note}
                onChange={(e) => setNewItem({ ...newItem, note: e.target.value })}
              />
              <select
                data-testid="admin-barn-new-status"
                className={inputCls}
                value={newItem.status}
                onChange={(e) => setNewItem({ ...newItem, status: e.target.value })}
              >
                <option value="coming-soon">Coming Soon</option>
                <option value="available">Available</option>
              </select>
            </div>
            <button
              data-testid="admin-barn-add-btn"
              type="submit"
              disabled={addingBarn}
              className="btn-pasture disabled:opacity-60"
            >
              {addingBarn ? "Adding…" : "Add to the Barn"}
            </button>
          </form>

          {/* Existing items list */}
          <ul data-testid="admin-barn-list" className="space-y-3">
            {barnItems.length === 0 && (
              <li className="text-sm text-[#6D5A50] italic">
                Nothing on the shelves yet.
              </li>
            )}
            {barnItems.map((item) => {
              const isEditing = editingSlug === item.slug;
              return (
                <li
                  key={item.slug}
                  data-testid={`admin-barn-row-${item.slug}`}
                  className="bg-[#F9F6F0] border border-[#E7DFD5] rounded-md p-4"
                >
                  {!isEditing ? (
                    <div className="flex items-center gap-4">
                      <div className="flex-grow min-w-0">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <span className="font-display text-xl text-[#4B3A2F] truncate">
                            {item.title}
                          </span>
                          <span className="text-xs uppercase tracking-[0.2em] text-[#D62839]">
                            {item.kind}
                          </span>
                          <span
                            className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${
                              item.status === "available"
                                ? "bg-[#7E9268] text-white"
                                : "bg-[#C8A876] text-[#2C2724]"
                            }`}
                          >
                            {item.status === "available" ? "Available" : "Coming Soon"}
                          </span>
                          <span className="text-[10px] text-[#6D5A50]">
                            #{item.order}
                          </span>
                        </div>
                        <p className="text-sm text-[#2C2724]/75 mt-1 line-clamp-1">
                          {item.note}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          data-testid={`admin-barn-edit-${item.slug}`}
                          onClick={() => startEdit(item)}
                          className="p-2 rounded-md border border-[#A87149] text-[#4B3A2F] hover:bg-[#A87149] hover:text-white transition-colors"
                          aria-label={`Edit ${item.title}`}
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          data-testid={`admin-barn-delete-${item.slug}`}
                          onClick={() => removeBarnItem(item.slug)}
                          className="p-2 rounded-md border border-[#D62839] text-[#D62839] hover:bg-[#D62839] hover:text-white transition-colors"
                          aria-label={`Delete ${item.title}`}
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input
                          className={inputCls}
                          placeholder="title"
                          value={draft.title}
                          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        />
                        <input
                          className={inputCls}
                          placeholder="small line under the title (optional)"
                          value={draft.subtitle || ""}
                          data-testid="admin-barn-edit-subtitle"
                          onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })}
                        />
                        <input
                          className={inputCls}
                          placeholder="kind"
                          value={draft.kind}
                          onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                        />
                        <input
                          type="number"
                          className={inputCls}
                          placeholder="order"
                          value={draft.order}
                          onChange={(e) => setDraft({ ...draft, order: e.target.value })}
                        />
                        <select
                          className={inputCls}
                          value={draft.status}
                          onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                        >
                          <option value="coming-soon">Coming Soon</option>
                          <option value="available">Available</option>
                        </select>
                        <textarea
                          rows={2}
                          className={`${inputCls} sm:col-span-2`}
                          placeholder="note"
                          value={draft.note}
                          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          data-testid={`admin-barn-save-${item.slug}`}
                          onClick={saveEdit}
                          className="px-4 py-2 rounded-md bg-[#7E9268] text-white text-sm flex items-center gap-2 hover:opacity-90"
                        >
                          <Check size={14} /> Save
                        </button>
                        <button
                          data-testid={`admin-barn-cancel-${item.slug}`}
                          onClick={cancelEdit}
                          className="px-4 py-2 rounded-md border border-[#A87149] text-[#4B3A2F] text-sm flex items-center gap-2 hover:bg-[#A87149]/10"
                        >
                          <X size={14} /> Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        {/* Saltie Winks — page content (every editable text block on the public page) */}
        <form
          onSubmit={saveSwp}
          data-testid="admin-saltie-winks-page-form"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            Saltie Winks™ — Page Content
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            every word on the public Saltie Winks page — edit freely
          </p>

          {!swp ? (
            <p className="text-sm text-[#6D5A50] italic">
              loading page content&hellip;
            </p>
          ) : (
            <div className="space-y-8">
              {/* SEO */}
              <div className="space-y-4">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  SEO (search & social)
                </p>
                <div>
                  <label className={labelCls}>SEO title</label>
                  <input
                    data-testid="admin-swp-seo-title"
                    className={inputCls}
                    value={swp.seo_title || ""}
                    onChange={(e) => setSwp({ ...swp, seo_title: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>SEO description</label>
                  <textarea
                    data-testid="admin-swp-seo-description"
                    rows={2}
                    className={inputCls}
                    value={swp.seo_description || ""}
                    onChange={(e) => setSwp({ ...swp, seo_description: e.target.value })}
                  />
                </div>
              </div>

              {/* Hero */}
              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Hero
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Hero title</label>
                    <input
                      data-testid="admin-swp-hero-title"
                      className={inputCls}
                      value={swp.hero_title || ""}
                      onChange={(e) => setSwp({ ...swp, hero_title: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Hero subtitle</label>
                    <input
                      data-testid="admin-swp-hero-subtitle"
                      className={inputCls}
                      value={swp.hero_subtitle || ""}
                      onChange={(e) => setSwp({ ...swp, hero_subtitle: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Signature quote */}
              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Signature quote (butterfly)
                </p>
                <div>
                  <label className={labelCls}>Quote — line 1</label>
                  <textarea
                    data-testid="admin-swp-quote-line1"
                    rows={2}
                    className={inputCls}
                    value={swp.quote_line1 || ""}
                    onChange={(e) => setSwp({ ...swp, quote_line1: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Quote — line 2{" "}
                    <span className="normal-case tracking-normal text-[#6D5A50]">
                      (shown in red)
                    </span>
                  </label>
                  <input
                    data-testid="admin-swp-quote-line2"
                    className={inputCls}
                    value={swp.quote_line2 || ""}
                    onChange={(e) => setSwp({ ...swp, quote_line2: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>Attribution</label>
                  <input
                    data-testid="admin-swp-quote-attribution"
                    className={inputCls}
                    value={swp.quote_attribution || ""}
                    onChange={(e) => setSwp({ ...swp, quote_attribution: e.target.value })}
                  />
                </div>
              </div>

              {/* What Is a Saltie Wink? */}
              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  What Is a Saltie Wink?
                </p>
                <div>
                  <label className={labelCls}>Heading</label>
                  <input
                    data-testid="admin-swp-what-is-heading"
                    className={inputCls}
                    value={swp.what_is_heading || ""}
                    onChange={(e) => setSwp({ ...swp, what_is_heading: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelCls}>
                    Body{" "}
                    <span className="normal-case tracking-normal text-[#6D5A50]">
                      (bold, italic, links, bullets — highlight text to format)
                    </span>
                  </label>
                  <div
                    data-testid="admin-swp-what-is-body-wysiwyg"
                    className="rounded-md border border-[#E7DFD5] bg-[#FBF7F0] overflow-hidden focus-within:border-[#B08A5A]"
                  >
                    <EditorProvider>
                      <Editor
                        value={swp.what_is_body_html || ""}
                        onChange={(e) => setSwp({ ...swp, what_is_body_html: e.target.value })}
                        containerProps={{
                          style: {
                            background: "#FBF7F0",
                            border: "none",
                            minHeight: "220px",
                            fontFamily: "Georgia, 'Times New Roman', serif",
                            fontSize: "15px",
                            lineHeight: "1.7",
                            color: "#2C2724",
                          },
                        }}
                      >
                        <Toolbar
                          style={{
                            background: "#F3EBDD",
                            borderBottom: "1px solid #E7DFD5",
                          }}
                        >
                          <BtnBold />
                          <BtnItalic />
                          <BtnUnderline />
                          <Separator />
                          <BtnBulletList />
                          <BtnNumberedList />
                          <Separator />
                          <BtnLink />
                          <BtnClearFormatting />
                        </Toolbar>
                      </Editor>
                    </EditorProvider>
                  </div>
                </div>
              </div>

              {/* Scrapbook heading */}
              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Scrapbook heading (above the wink list)
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>
                      Kicker{" "}
                      <span className="normal-case tracking-normal text-[#6D5A50]">
                        (small caps line)
                      </span>
                    </label>
                    <input
                      data-testid="admin-swp-scrapbook-kicker"
                      className={inputCls}
                      value={swp.scrapbook_kicker || ""}
                      onChange={(e) => setSwp({ ...swp, scrapbook_kicker: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>
                      Heading{" "}
                      <span className="normal-case tracking-normal text-[#6D5A50]">
                        (the ™ is added automatically)
                      </span>
                    </label>
                    <input
                      data-testid="admin-swp-scrapbook-heading"
                      className={inputCls}
                      value={swp.scrapbook_heading || ""}
                      onChange={(e) => setSwp({ ...swp, scrapbook_heading: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Closing blessing */}
              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Closing blessing (script line above Saltie Selfies)
                </p>
                <div>
                  <label className={labelCls}>
                    Blessing{" "}
                    <span className="normal-case tracking-normal text-[#6D5A50]">
                      (bold, italic, links, bullets)
                    </span>
                  </label>
                  <div
                    data-testid="admin-swp-closing-blessing-wysiwyg"
                    className="rounded-md border border-[#E7DFD5] bg-[#FBF7F0] overflow-hidden focus-within:border-[#B08A5A]"
                  >
                    <EditorProvider>
                      <Editor
                        value={swp.closing_blessing_html || ""}
                        onChange={(e) => setSwp({ ...swp, closing_blessing_html: e.target.value })}
                        containerProps={{
                          style: {
                            background: "#FBF7F0",
                            border: "none",
                            minHeight: "150px",
                            fontFamily: "Georgia, 'Times New Roman', serif",
                            fontSize: "15px",
                            lineHeight: "1.7",
                            color: "#2C2724",
                          },
                        }}
                      >
                        <Toolbar
                          style={{
                            background: "#F3EBDD",
                            borderBottom: "1px solid #E7DFD5",
                          }}
                        >
                          <BtnBold />
                          <BtnItalic />
                          <BtnUnderline />
                          <Separator />
                          <BtnBulletList />
                          <BtnNumberedList />
                          <Separator />
                          <BtnLink />
                          <BtnClearFormatting />
                        </Toolbar>
                      </Editor>
                    </EditorProvider>
                  </div>
                </div>
              </div>

              {/* Saltie Selfies section labels */}
              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Saltie Selfies section
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Section heading</label>
                    <input
                      data-testid="admin-swp-selfies-heading"
                      className={inputCls}
                      value={swp.selfies_heading || ""}
                      onChange={(e) => setSwp({ ...swp, selfies_heading: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Tagline (script line)</label>
                    <input
                      data-testid="admin-swp-selfies-tagline"
                      className={inputCls}
                      value={swp.selfies_tagline || ""}
                      onChange={(e) => setSwp({ ...swp, selfies_tagline: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>
                      Kicker (when photos exist)
                    </label>
                    <input
                      data-testid="admin-swp-selfies-kicker-with"
                      className={inputCls}
                      value={swp.selfies_kicker_with_photos || ""}
                      onChange={(e) => setSwp({ ...swp, selfies_kicker_with_photos: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>
                      Kicker prefix (when empty){" "}
                      <span className="normal-case tracking-normal text-[#6D5A50]">
                        (comes before the flashing label)
                      </span>
                    </label>
                    <input
                      data-testid="admin-swp-selfies-kicker-prefix"
                      className={inputCls}
                      value={swp.selfies_kicker_without_photos_prefix || ""}
                      onChange={(e) => setSwp({ ...swp, selfies_kicker_without_photos_prefix: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Flashing label (when empty)</label>
                    <input
                      data-testid="admin-swp-selfies-coming-soon"
                      className={inputCls}
                      value={swp.selfies_kicker_coming_soon || ""}
                      onChange={(e) => setSwp({ ...swp, selfies_kicker_coming_soon: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Footer (when photos exist)</label>
                    <input
                      data-testid="admin-swp-selfies-footer-with"
                      className={inputCls}
                      value={swp.selfies_footer_with_photos || ""}
                      onChange={(e) => setSwp({ ...swp, selfies_footer_with_photos: e.target.value })}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Footer (when empty)</label>
                    <textarea
                      data-testid="admin-swp-selfies-footer-empty"
                      rows={2}
                      className={inputCls}
                      value={swp.selfies_footer_without_photos || ""}
                      onChange={(e) => setSwp({ ...swp, selfies_footer_without_photos: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Three cards */}
              <div className="space-y-4 pt-6 border-t border-[#E7DFD5]">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#D62839]">
                  Notice · Remember · Grow — the three cards
                </p>

                {[
                  { key: "notice", label: "Notice" },
                  { key: "remember", label: "Remember" },
                  { key: "grow", label: "Grow" },
                ].map(({ key, label }) => (
                  <div key={key} className="p-4 rounded-md bg-[#F9F6F0] border border-[#E7DFD5]">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#6D5A50] mb-3">
                      {label} card
                    </p>
                    <div className="grid sm:grid-cols-[1fr,2fr] gap-3">
                      <div>
                        <label className={labelCls}>Title</label>
                        <input
                          data-testid={`admin-swp-card-${key}-title`}
                          className={inputCls}
                          value={swp[`card_${key}_title`] || ""}
                          onChange={(e) =>
                            setSwp({ ...swp, [`card_${key}_title`]: e.target.value })
                          }
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Body</label>
                        <textarea
                          data-testid={`admin-swp-card-${key}-body`}
                          rows={2}
                          className={inputCls}
                          value={swp[`card_${key}_body`] || ""}
                          onChange={(e) =>
                            setSwp({ ...swp, [`card_${key}_body`]: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                data-testid="admin-swp-save-btn"
                type="submit"
                disabled={savingSwp}
                className="btn-pasture mt-2 disabled:opacity-60"
              >
                {savingSwp ? "Saving…" : "Save Saltie Winks page"}
              </button>
            </div>
          )}
        </form>

        {/* Saltie Winks editor */}
        <section
          data-testid="admin-saltie-winks-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            Saltie Winks™
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            the growing scrapbook of quiet God-moments
          </p>

          {/* Add a new wink */}
          <form
            onSubmit={addWink}
            data-testid="admin-wink-add-form"
            className="mb-8 p-5 rounded-md bg-[#F9F6F0] border border-dashed border-[#7E9268]"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-4 flex items-center gap-2">
              <Plus size={14} /> Add a new wink
            </p>
            <div className="space-y-3">
              <textarea
                data-testid="admin-wink-new-text"
                rows={3}
                className={inputCls}
                placeholder="the wink — the moment itself (a butterfly at the window, a breeze that felt like a hug…)"
                value={newWink.wink}
                onChange={(e) => setNewWink({ ...newWink, wink: e.target.value })}
              />
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  data-testid="admin-wink-new-when"
                  className={inputCls}
                  placeholder="when (e.g. September 2026 or 2026-09-14)"
                  value={newWink.when}
                  onChange={(e) => setNewWink({ ...newWink, when: e.target.value })}
                />
                <input
                  data-testid="admin-wink-new-context"
                  className={inputCls}
                  placeholder="context (optional — a short setting or note)"
                  value={newWink.context}
                  onChange={(e) => setNewWink({ ...newWink, context: e.target.value })}
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-[#4B3A2F]">
                <input
                  data-testid="admin-wink-new-published"
                  type="checkbox"
                  checked={newWink.published}
                  onChange={(e) =>
                    setNewWink({ ...newWink, published: e.target.checked })
                  }
                />
                published (visible on the scrapbook page)
              </label>
            </div>
            <button
              data-testid="admin-wink-add-btn"
              type="submit"
              disabled={addingWink}
              className="btn-pasture mt-4 disabled:opacity-60"
            >
              {addingWink ? "Adding…" : "Pin this wink"}
            </button>
          </form>

          {/* Existing winks */}
          <ul data-testid="admin-winks-list" className="space-y-3">
            {winks.length === 0 && (
              <li className="text-sm text-[#6D5A50] italic">
                No winks yet.
              </li>
            )}
            {winks.map((w) => {
              const isEditing = editingWinkId === w.id;
              return (
                <li
                  key={w.id}
                  data-testid={`admin-wink-row-${w.id}`}
                  className="bg-[#F9F6F0] border border-[#E7DFD5] rounded-md p-4"
                >
                  {!isEditing ? (
                    <div className="flex items-start gap-4">
                      <div className="flex-grow min-w-0">
                        <div className="flex items-baseline gap-3 flex-wrap mb-1">
                          <span className="text-[10px] uppercase tracking-[0.25em] text-[#D62839]">
                            {w.when}
                          </span>
                          <span
                            className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${
                              w.published
                                ? "bg-[#7E9268] text-white"
                                : "bg-[#C8A876] text-[#2C2724]"
                            }`}
                          >
                            {w.published ? "Published" : "Draft"}
                          </span>
                        </div>
                        <p className="font-script text-xl text-[#4B3A2F] leading-snug">
                          {w.wink}
                        </p>
                        {w.context && (
                          <p className="text-sm text-[#2C2724]/70 mt-1 italic">
                            {w.context}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button
                          data-testid={`admin-wink-edit-${w.id}`}
                          onClick={() => startEditWink(w)}
                          className="p-2 rounded-md border border-[#A87149] text-[#4B3A2F] hover:bg-[#A87149] hover:text-white transition-colors"
                          aria-label="Edit wink"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          data-testid={`admin-wink-delete-${w.id}`}
                          onClick={() => removeWink(w.id)}
                          className="p-2 rounded-md border border-[#D62839] text-[#D62839] hover:bg-[#D62839] hover:text-white transition-colors"
                          aria-label="Delete wink"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <textarea
                        rows={3}
                        className={inputCls}
                        placeholder="the wink"
                        value={winkDraft.wink}
                        onChange={(e) =>
                          setWinkDraft({ ...winkDraft, wink: e.target.value })
                        }
                      />
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input
                          className={inputCls}
                          placeholder="when"
                          value={winkDraft.when}
                          onChange={(e) =>
                            setWinkDraft({ ...winkDraft, when: e.target.value })
                          }
                        />
                        <input
                          className={inputCls}
                          placeholder="context"
                          value={winkDraft.context || ""}
                          onChange={(e) =>
                            setWinkDraft({ ...winkDraft, context: e.target.value })
                          }
                        />
                      </div>
                      <label className="inline-flex items-center gap-2 text-sm text-[#4B3A2F]">
                        <input
                          type="checkbox"
                          checked={winkDraft.published}
                          onChange={(e) =>
                            setWinkDraft({ ...winkDraft, published: e.target.checked })
                          }
                        />
                        published
                      </label>
                      <div className="flex gap-2">
                        <button
                          data-testid={`admin-wink-save-${w.id}`}
                          onClick={saveEditWink}
                          className="px-4 py-2 rounded-md bg-[#7E9268] text-white text-sm flex items-center gap-2 hover:opacity-90"
                        >
                          <Check size={14} /> Save
                        </button>
                        <button
                          data-testid={`admin-wink-cancel-${w.id}`}
                          onClick={cancelEditWink}
                          className="px-4 py-2 rounded-md border border-[#A87149] text-[#4B3A2F] text-sm flex items-center gap-2 hover:bg-[#A87149]/10"
                        >
                          <X size={14} /> Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>

        {/* Available Animals — Looking for New Pastures */}
        <section
          data-testid="admin-available-animals-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            Available Animals Manager
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-2">
            manage the Looking for New Pastures page
          </p>
          <p className="text-sm text-[#6D5A50] italic mb-6">
            Add, edit, reorder, hide, and update every listing on the public
            <span className="mx-1 font-body">/looking-for-new-pastures</span>
            page. Upload the featured photo, any number of FAQ flyer pages (JPGs),
            the full FAQ PDF, and any number of gallery photos with captions.
            Set an animal's status to <em>Placed</em> to soften the card and
            display a tasteful "FOUND THEIR NEW PASTURE" banner while keeping
            the listing visible.
          </p>
          <AdminAvailableAnimals passcode={passcode} />
        </section>

        {/* Character Photos */}
        <section
          data-testid="admin-photos-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12"
        >
          <h2 className="font-display text-3xl text-[#4B3A2F] mb-1">
            Character Photos
          </h2>
          <p className="font-script text-xl text-[#4B3A2F] mb-2">
            drop in real photos of the herd &amp; family
          </p>
          <p className="text-sm text-[#6D5A50] italic mb-6">
            Upload any image — the site auto-crops it to a square, resizes to 1200 × 1200, and
            replaces the placeholder on the Meet the Herd, Saltie Brays, Saltie Moos, and Meet the
            Family pages. No compression app needed.
            <span className="block mt-2 text-[#4B3A2F]">
              Tip: hover a card to see Replace / Remove — swap a photo any time.
              For couples &amp; groups (Stefanie&nbsp;&amp;&nbsp;Mike, Alexis&nbsp;&amp;&nbsp;Ethan,
              Tyler·Dawson·Saylor) the crop tool switches to a 4:3 landscape frame automatically.
            </span>
          </p>

          {PHOTO_ROSTER.map((group) => (
            <div key={group.group} className="mb-8">
              <h3 className="text-xs uppercase tracking-[0.3em] text-[#D62839] mb-4">
                {group.group}
              </h3>
              <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {group.items.map(({ slug, name }) => {
                  const rec = photos[slug];
                  const hasPhoto = !!rec?.url;
                  const uploading = uploadingSlug === slug;
                  return (
                    <li
                      key={slug}
                      data-testid={`admin-photo-${slug}`}
                      className="relative bg-[#F9F6F0] border border-[#E7DFD5] rounded-md overflow-hidden"
                    >
                      <div className="relative aspect-square bg-[#EFE4CE]">
                        {hasPhoto ? (
                          <img
                            src={rec.url}
                            alt={name}
                            className="absolute inset-0 w-full h-full object-cover"
                            draggable="false"
                          />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-[#8D6E63]">
                            <Camera size={30} />
                          </div>
                        )}
                        {uploading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-[#4B3A2F]/60 text-white text-xs uppercase tracking-widest">
                            uploading…
                          </div>
                        )}
                      </div>
                      <div className="px-3 py-2 text-center">
                        <p className="font-display text-lg text-[#4B3A2F] leading-tight truncate">
                          {name}
                        </p>
                        <div className="mt-2 flex flex-col gap-1.5">
                          <label
                            className={`inline-block cursor-pointer text-[11px] uppercase tracking-widest text-center px-3 py-1.5 rounded-md text-white ${
                              uploading ? "bg-[#8D6E63]" : "bg-[#7E9268] hover:opacity-90"
                            } transition-opacity`}
                          >
                            {hasPhoto ? "Replace" : "Upload"}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              disabled={uploading}
                              data-testid={`admin-photo-upload-${slug}`}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) setCropTarget({ slug, name, file: f });
                                e.target.value = "";
                              }}
                            />
                          </label>
                          {hasPhoto && (
                            <button
                              type="button"
                              data-testid={`admin-photo-delete-${slug}`}
                              onClick={() => removePhoto(slug)}
                              className="text-[10px] uppercase tracking-widest text-[#D62839] hover:underline"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </section>

        {/* Saltie Fun Facts editor */}
        <section
          data-testid="admin-fun-facts-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12"
        >
          <div className="flex items-baseline gap-3 mb-1">
            <BookOpen size={20} className="text-[#4B3A2F]" />
            <h2 className="font-display text-3xl text-[#4B3A2F]">
              Saltie Fun Facts
            </h2>
          </div>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            names on the wooden plaques + fact tiles
          </p>

          {funFacts && (
            <>
              {/* Character name inputs */}
              <div className="mb-8">
                <h3 className="font-display text-xl text-[#4B3A2F] mb-3">
                  Moo photo plaques (7)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {funFacts.moo_characters.map((c, i) => (
                    <input
                      key={c.slug}
                      data-testid={`fun-moo-name-${i}`}
                      type="text"
                      placeholder={`Moo ${i + 1} name`}
                      value={c.name}
                      onChange={(e) => {
                        const next = { ...funFacts };
                        next.moo_characters = [...next.moo_characters];
                        next.moo_characters[i] = { ...c, name: e.target.value };
                        setFunFacts(next);
                      }}
                      className="border border-[#D9C9A8] rounded px-3 py-2 bg-white font-body text-[#2C2724]"
                    />
                  ))}
                </div>

                <h3 className="font-display text-xl text-[#4B3A2F] mt-6 mb-3">
                  Bray photo plaques (4)
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {funFacts.bray_characters.map((c, i) => (
                    <input
                      key={c.slug}
                      data-testid={`fun-bray-name-${i}`}
                      type="text"
                      placeholder={`Bray ${i + 1} name`}
                      value={c.name}
                      onChange={(e) => {
                        const next = { ...funFacts };
                        next.bray_characters = [...next.bray_characters];
                        next.bray_characters[i] = { ...c, name: e.target.value };
                        setFunFacts(next);
                      }}
                      className="border border-[#D9C9A8] rounded px-3 py-2 bg-white font-body text-[#2C2724]"
                    />
                  ))}
                </div>
              </div>

              {/* Fact tile editors */}
              {[
                { key: "moo_facts", label: "Moo Fact Tiles" },
                { key: "bray_facts", label: "Bray Fact Tiles" },
              ].map(({ key, label }) => (
                <div key={key} className="mb-8">
                  <h3 className="font-display text-xl text-[#4B3A2F] mb-3">
                    {label} ({funFacts[key].length})
                  </h3>
                  <div className="space-y-3">
                    {funFacts[key].map((f, i) => (
                      <div
                        key={i}
                        data-testid={`fun-${key}-${i}`}
                        className="border border-[#D9C9A8] rounded p-3 bg-[#FBF6EB] grid grid-cols-12 gap-2"
                      >
                        <input
                          type="text"
                          placeholder="Icon (lucide name, e.g. Snowflake)"
                          value={f.icon}
                          onChange={(e) => {
                            const next = { ...funFacts };
                            next[key] = [...next[key]];
                            next[key][i] = { ...f, icon: e.target.value };
                            setFunFacts(next);
                          }}
                          className="col-span-3 border border-[#D9C9A8] rounded px-2 py-1 bg-white text-sm"
                        />
                        <input
                          type="color"
                          value={f.color}
                          onChange={(e) => {
                            const next = { ...funFacts };
                            next[key] = [...next[key]];
                            next[key][i] = { ...f, color: e.target.value };
                            setFunFacts(next);
                          }}
                          className="col-span-1 h-9 rounded"
                        />
                        <input
                          type="text"
                          placeholder="TITLE (uppercase)"
                          value={f.title}
                          onChange={(e) => {
                            const next = { ...funFacts };
                            next[key] = [...next[key]];
                            next[key][i] = { ...f, title: e.target.value };
                            setFunFacts(next);
                          }}
                          className="col-span-3 border border-[#D9C9A8] rounded px-2 py-1 bg-white text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Fact body text"
                          value={f.body}
                          onChange={(e) => {
                            const next = { ...funFacts };
                            next[key] = [...next[key]];
                            next[key][i] = { ...f, body: e.target.value };
                            setFunFacts(next);
                          }}
                          className="col-span-5 border border-[#D9C9A8] rounded px-2 py-1 bg-white text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <p className="text-xs text-[#6D5A50] italic mb-3">
                Icon names come from the lucide-react library (case-sensitive) — e.g. Snowflake, Wind, Sparkles, Leaf, Heart, Globe, Ear, Shield, Footprints, Smile, Star.
              </p>

              <button
                data-testid="save-fun-facts-btn"
                disabled={savingFunFacts}
                onClick={async () => {
                  setSavingFunFacts(true);
                  try {
                    const saved = await updateSaltieFunFacts(funFacts, passcode);
                    setFunFacts(saved);
                    toast("Fun Facts saved.");
                  } catch {
                    toast("Couldn't save fun facts.");
                  } finally {
                    setSavingFunFacts(false);
                  }
                }}
                className="px-6 py-2 rounded-full bg-[#4B3A2F] text-[#F7F0E4] hover:bg-[#3E2F24] transition-colors"
              >
                {savingFunFacts ? "Saving…" : "Save Fun Facts"}
              </button>
            </>
          )}
        </section>

        {/* Coloring Pages */}
        <section
          data-testid="admin-coloring-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12"
        >
          <div className="flex items-baseline gap-3 mb-1">
            <BookOpen size={20} className="text-[#4B3A2F]" />
            <h2 className="font-display text-3xl text-[#4B3A2F]">
              Coloring Pages
            </h2>
          </div>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            upload printable coloring pages for the Story Barn
          </p>

          {/* Upload form */}
          <div className="bg-[#FBF6EB] border border-[#D9C9A8] rounded p-4 mb-6">
            <h3 className="font-display text-xl text-[#4B3A2F] mb-3">
              Upload a new page
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                data-testid="coloring-new-title"
                type="text"
                placeholder="Title (e.g. Spread the Love Like Fire)"
                value={newColoring.title}
                onChange={(e) => setNewColoring((v) => ({ ...v, title: e.target.value }))}
                className="md:col-span-2 border border-[#D9C9A8] rounded px-3 py-2 bg-white"
              />
              <input
                data-testid="coloring-new-verse"
                type="text"
                placeholder="Verse (e.g. 2 Timothy 1:6)"
                value={newColoring.verse}
                onChange={(e) => setNewColoring((v) => ({ ...v, verse: e.target.value }))}
                className="border border-[#D9C9A8] rounded px-3 py-2 bg-white"
              />
              <input
                data-testid="coloring-new-order"
                type="number"
                placeholder="Sort order"
                value={newColoring.order}
                onChange={(e) => setNewColoring((v) => ({ ...v, order: Number(e.target.value) }))}
                className="border border-[#D9C9A8] rounded px-3 py-2 bg-white"
              />
              <input
                data-testid="coloring-new-file"
                type="file"
                accept="image/png,image/jpeg,image/webp,application/pdf"
                onChange={(e) => setNewColoring((v) => ({ ...v, file: e.target.files?.[0] || null }))}
                className="md:col-span-3 border border-[#D9C9A8] rounded px-3 py-2 bg-white"
              />
              <button
                data-testid="coloring-upload-btn"
                disabled={!newColoring.file || !newColoring.title || uploadingColoring}
                onClick={async () => {
                  setUploadingColoring(true);
                  try {
                    await uploadColoringPage(newColoring, passcode);
                    const list = await getColoringPages();
                    setColoringPages(list);
                    setNewColoring({ title: "", verse: "", order: 0, file: null });
                    toast("Coloring page uploaded.");
                  } catch (err) {
                    toast("Upload failed: " + (err?.response?.data?.detail || err.message));
                  } finally {
                    setUploadingColoring(false);
                  }
                }}
                className="px-4 py-2 rounded-full bg-[#4B3A2F] text-[#F7F0E4] hover:bg-[#3E2F24] transition-colors disabled:opacity-50"
              >
                {uploadingColoring ? "Uploading…" : "Upload"}
              </button>
            </div>
          </div>

          {/* Existing pages list */}
          <div className="space-y-3" data-testid="coloring-list">
            {coloringPages.length === 0 && (
              <p className="text-[#6D5A50] italic">No coloring pages yet.</p>
            )}
            {coloringPages.map((p) => (
              <div
                key={p.slug}
                data-testid={`coloring-row-${p.slug}`}
                className="border border-[#D9C9A8] rounded p-3 bg-white grid grid-cols-12 gap-3 items-center"
              >
                <img
                  src={p.file_url.startsWith("http") || p.file_url.startsWith("/coloring/") ? p.file_url : `${process.env.REACT_APP_BACKEND_URL || ""}${p.file_url}`}
                  alt={p.title}
                  className="col-span-2 w-full h-20 object-contain bg-[#FBF6EB] rounded"
                />
                <input
                  type="text"
                  value={p.title}
                  onChange={(e) => {
                    const next = [...coloringPages];
                    const i = next.findIndex((x) => x.slug === p.slug);
                    next[i] = { ...p, title: e.target.value };
                    setColoringPages(next);
                  }}
                  className="col-span-3 border border-[#D9C9A8] rounded px-2 py-1"
                />
                <input
                  type="text"
                  value={p.verse || ""}
                  placeholder="Verse"
                  onChange={(e) => {
                    const next = [...coloringPages];
                    const i = next.findIndex((x) => x.slug === p.slug);
                    next[i] = { ...p, verse: e.target.value };
                    setColoringPages(next);
                  }}
                  className="col-span-2 border border-[#D9C9A8] rounded px-2 py-1"
                />
                <input
                  type="number"
                  value={p.order ?? 0}
                  onChange={(e) => {
                    const next = [...coloringPages];
                    const i = next.findIndex((x) => x.slug === p.slug);
                    next[i] = { ...p, order: Number(e.target.value) };
                    setColoringPages(next);
                  }}
                  className="col-span-1 border border-[#D9C9A8] rounded px-2 py-1"
                />
                <button
                  data-testid={`coloring-save-${p.slug}`}
                  onClick={async () => {
                    try {
                      await updateColoringPage(p.slug, { title: p.title, verse: p.verse || "", order: p.order ?? 0 }, passcode);
                      toast("Saved.");
                    } catch {
                      toast("Save failed.");
                    }
                  }}
                  className="col-span-2 px-3 py-1.5 rounded-full bg-[#4B3A2F] text-[#F7F0E4] hover:bg-[#3E2F24] text-sm"
                >
                  Save
                </button>
                <button
                  data-testid={`coloring-delete-${p.slug}`}
                  onClick={async () => {
                    if (!window.confirm(`Delete "${p.title}"?`)) return;
                    try {
                      await deleteColoringPage(p.slug, passcode);
                      setColoringPages(coloringPages.filter((x) => x.slug !== p.slug));
                      toast("Deleted.");
                    } catch {
                      toast("Delete failed.");
                    }
                  }}
                  className="col-span-2 px-3 py-1.5 rounded-full border border-[#B85A5A] text-[#B85A5A] hover:bg-[#B85A5A] hover:text-white text-sm"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Character Bios */}
        <section
          data-testid="admin-bios-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12"
        >
          <div className="flex items-baseline gap-3 mb-1">
            <BookOpen size={20} className="text-[#4B3A2F]" />
            <h2 className="font-display text-3xl text-[#4B3A2F]">
              Character Bios
            </h2>
          </div>
          <p className="font-script text-xl text-[#4B3A2F] mb-2">
            polish each animal &amp; family bio without touching code
          </p>
          <p className="text-sm text-[#6D5A50] italic mb-6">
            Changes save per-character. &ldquo;Role&rdquo; is the small
            uppercase line above the name; &ldquo;Bio&rdquo; is the paragraph
            underneath.
          </p>

          {bios.length === 0 ? (
            <p className="text-sm text-[#6D5A50] italic">loading…</p>
          ) : (
            <ul data-testid="admin-bios-list" className="space-y-4">
              {bios.map((b) => (
                <li
                  key={b.slug}
                  data-testid={`admin-bio-row-${b.slug}`}
                  className="bg-[#F9F6F0] border border-[#E7DFD5] rounded-md p-4"
                >
                  <div className="flex items-baseline gap-3 mb-3 flex-wrap">
                    <span className="font-display text-xl text-[#4B3A2F]">
                      {b.slug}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#6D5A50]">
                      {(b.updated_at || "").slice(0, 10)}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <input
                      data-testid={`admin-bio-role-${b.slug}`}
                      className={inputCls}
                      placeholder="Role / short eyebrow line"
                      value={bioDrafts[b.slug]?.role ?? ""}
                      onChange={(e) =>
                        setBioDrafts({
                          ...bioDrafts,
                          [b.slug]: {
                            ...bioDrafts[b.slug],
                            role: e.target.value,
                          },
                        })
                      }
                    />
                    <textarea
                      data-testid={`admin-bio-intro-${b.slug}`}
                      rows={4}
                      className={inputCls}
                      placeholder="Full bio paragraph"
                      value={bioDrafts[b.slug]?.intro ?? ""}
                      onChange={(e) =>
                        setBioDrafts({
                          ...bioDrafts,
                          [b.slug]: {
                            ...bioDrafts[b.slug],
                            intro: e.target.value,
                          },
                        })
                      }
                    />
                  </div>
                  <button
                    type="button"
                    data-testid={`admin-bio-save-${b.slug}`}
                    onClick={() => saveBio(b.slug)}
                    disabled={savingBio === b.slug}
                    className="btn-pasture mt-3 disabled:opacity-60 inline-flex items-center gap-2"
                  >
                    <Check size={14} />
                    {savingBio === b.slug ? "Saving…" : "Save bio"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Saltie Selfies */}
        <section
          data-testid="admin-selfies-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12"
        >
          <div className="flex items-baseline gap-3 mb-1">
            <ImageIcon size={20} className="text-[#4B3A2F]" />
            <h2 className="font-display text-3xl text-[#4B3A2F]">
              Saltie Selfies
            </h2>
          </div>
          <p className="font-script text-xl text-[#4B3A2F] mb-2">
            the growing collage on the Saltie Winks page
          </p>
          <p className="text-sm text-[#6D5A50] italic mb-6">
            Every selfie you upload lands on the Saltie Winks page as a rotated
            polaroid. Add as many as you like — the grid grows to fit.
          </p>

          {/* Upload form */}
          <div
            data-testid="admin-selfie-add"
            className="mb-8 p-5 rounded-md bg-[#F9F6F0] border border-dashed border-[#7E9268]"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-4 flex items-center gap-2">
              <Plus size={14} /> Add a new selfie
            </p>
            <input
              data-testid="admin-selfie-caption"
              className={`${inputCls} mb-3`}
              placeholder="Caption (optional — shown on hover under the photo)"
              value={selfieCaptionDraft}
              onChange={(e) => setSelfieCaptionDraft(e.target.value)}
            />
            <label
              className={`inline-block cursor-pointer text-xs uppercase tracking-widest px-4 py-2 rounded-md text-white ${
                uploadingSelfie ? "bg-[#8D6E63]" : "bg-[#7E9268] hover:opacity-90"
              } transition-opacity`}
            >
              {uploadingSelfie ? "Uploading…" : "Upload photo"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploadingSelfie}
                data-testid="admin-selfie-upload"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadSelfie(f);
                  e.target.value = "";
                }}
              />
            </label>
          </div>

          {/* Existing selfies */}
          {selfies.length === 0 ? (
            <p
              data-testid="admin-selfies-empty"
              className="text-sm text-[#6D5A50] italic"
            >
              No selfies yet — the pasture is quiet.
            </p>
          ) : (
            <ul
              data-testid="admin-selfies-list"
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4"
            >
              {selfies.map((s) => {
                const isEditing = editingSelfieId === s.id;
                const abs = s.url && s.url.startsWith("http")
                  ? s.url
                  : `${process.env.REACT_APP_BACKEND_URL || ""}${s.url}`;
                return (
                  <li
                    key={s.id}
                    data-testid={`admin-selfie-${s.id}`}
                    className="bg-[#F9F6F0] border border-[#E7DFD5] rounded-md overflow-hidden"
                  >
                    <div className="relative aspect-square bg-[#EFE4CE]">
                      <img
                        src={abs}
                        alt={s.caption || "Saltie Selfie"}
                        className="absolute inset-0 w-full h-full object-cover"
                        draggable="false"
                      />
                    </div>
                    <div className="p-3 space-y-2">
                      {isEditing ? (
                        <>
                          <input
                            className={inputCls}
                            placeholder="caption"
                            value={selfieDraft.caption}
                            onChange={(e) =>
                              setSelfieDraft({ ...selfieDraft, caption: e.target.value })
                            }
                          />
                          <input
                            type="number"
                            className={inputCls}
                            placeholder="order (lower = earlier)"
                            value={selfieDraft.order}
                            onChange={(e) =>
                              setSelfieDraft({ ...selfieDraft, order: e.target.value })
                            }
                          />
                          <div className="flex gap-2">
                            <button
                              type="button"
                              data-testid={`admin-selfie-save-${s.id}`}
                              onClick={saveEditSelfie}
                              className="px-3 py-1.5 rounded-md bg-[#7E9268] text-white text-xs flex items-center gap-1 hover:opacity-90"
                            >
                              <Check size={12} /> Save
                            </button>
                            <button
                              type="button"
                              data-testid={`admin-selfie-cancel-${s.id}`}
                              onClick={cancelEditSelfie}
                              className="px-3 py-1.5 rounded-md border border-[#A87149] text-[#4B3A2F] text-xs flex items-center gap-1 hover:bg-[#A87149]/10"
                            >
                              <X size={12} /> Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          {s.caption ? (
                            <p className="text-xs text-[#2C2724] italic line-clamp-2">
                              {s.caption}
                            </p>
                          ) : (
                            <p className="text-xs text-[#6D5A50]/60 italic">
                              (no caption)
                            </p>
                          )}
                          <p className="text-[10px] uppercase tracking-[0.25em] text-[#6D5A50]">
                            #{s.order}
                          </p>
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              data-testid={`admin-selfie-edit-${s.id}`}
                              onClick={() => startEditSelfie(s)}
                              className="p-1.5 rounded-md border border-[#A87149] text-[#4B3A2F] hover:bg-[#A87149] hover:text-white transition-colors"
                              aria-label="Edit selfie"
                              title="Edit"
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              type="button"
                              data-testid={`admin-selfie-delete-${s.id}`}
                              onClick={() => removeSelfie(s.id)}
                              className="p-1.5 rounded-md border border-[#D62839] text-[#D62839] hover:bg-[#D62839] hover:text-white transition-colors"
                              aria-label="Delete selfie"
                              title="Delete"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Audiobook uploader */}
        <section
          data-testid="admin-audiobook-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12"
        >
          <div className="flex items-baseline gap-3 mb-1">
            <Radio size={20} className="text-[#4B3A2F]" />
            <h2 className="font-display text-3xl text-[#4B3A2F]">
              Audiobook
            </h2>
          </div>
          <p className="font-script text-xl text-[#4B3A2F] mb-2">
            the wooden radio on the Story Barn shelf
          </p>
          <p className="text-sm text-[#6D5A50] italic mb-6">
            Upload a single MP3 (up to 200&nbsp;MB) and the wooden-radio player
            on the Story Barn page turns on automatically. Replace it any time —
            listeners will always hear the latest file.
          </p>

          <div className="grid sm:grid-cols-2 gap-3 mb-5">
            <div>
              <label className={labelCls}>Title</label>
              <input
                data-testid="admin-audiobook-title"
                className={inputCls}
                value={audiobookTitle}
                onChange={(e) => setAudiobookTitle(e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Subtitle</label>
              <input
                data-testid="admin-audiobook-subtitle"
                className={inputCls}
                value={audiobookSubtitle}
                onChange={(e) => setAudiobookSubtitle(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-5">
            <button
              type="button"
              data-testid="admin-audiobook-save-meta-btn"
              onClick={saveAudiobookMeta}
              disabled={savingAudiobookMeta}
              className="btn-pasture disabled:opacity-60 inline-flex items-center gap-2"
            >
              <Check size={14} />
              {savingAudiobookMeta ? "Saving…" : "Save details"}
            </button>
          </div>

          <div className="rounded-md bg-[#F9F6F0] border border-dashed border-[#A87149] p-5 mb-4">
            <p className="text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-3">
              {audiobook?.available ? "Replace the MP3" : "Upload the MP3"}
            </p>
            <label
              className={`inline-block cursor-pointer text-xs uppercase tracking-widest px-4 py-2 rounded-md text-white ${
                uploadingAudiobook ? "bg-[#8D6E63]" : "bg-[#7E9268] hover:opacity-90"
              } transition-opacity`}
            >
              {uploadingAudiobook ? "Uploading…" : audiobook?.available ? "Replace file" : "Choose an MP3"}
              <input
                type="file"
                accept="audio/mpeg,audio/mp3,.mp3"
                className="hidden"
                disabled={uploadingAudiobook}
                data-testid="admin-audiobook-upload"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleAudiobookUpload(f);
                  e.target.value = "";
                }}
              />
            </label>
            {audiobook?.available && (
              <p className="mt-3 text-xs text-[#6D5A50]">
                Current file: <span className="font-body text-[#2C2724]">audiobook.mp3</span>
                {" · "}
                <span className="tabular-nums">{Math.round((audiobook.size || 0) / 1024 / 1024)} MB</span>
              </p>
            )}
          </div>

          {audiobook?.available && (
            <button
              type="button"
              data-testid="admin-audiobook-delete-btn"
              onClick={removeAudiobook}
              className="text-xs uppercase tracking-widest text-[#D62839] hover:underline"
            >
              Remove audiobook
            </button>
          )}
        </section>

        {/* Our Cow-munity Manager — link out to the dedicated editor */}
        <section
          data-testid="admin-cowmunity-link-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <div className="flex items-baseline gap-3 mb-1">
              <Users size={20} className="text-[#4B3A2F]" />
              <h2 className="font-display text-3xl text-[#4B3A2F]">
                Our Cow-munity Manager
              </h2>
            </div>
            <p className="font-script text-xl text-[#4B3A2F] mb-1">
              tend the Salt Trail
            </p>
            <p className="text-sm text-[#6D5A50] italic max-w-md">
              Edit every field on the Our Cow-munity page, upload the four
              Around-the-Pasture photos, hide individual directory cards, and
              swap the Google Form / Sheet URLs anytime.
            </p>
          </div>
          <a
            href="/admin/cow-munity"
            data-testid="admin-cowmunity-open-btn"
            className="btn-pasture inline-flex items-center gap-2"
          >
            <Users size={16} />
            Open Cow-munity
          </a>
        </section>

        {/* Subscribers Dashboard — full CRUD lives on its own admin page */}
        <section
          data-testid="admin-subscribers-link-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <div className="flex items-baseline gap-3 mb-1">
              <Users size={20} className="text-[#4B3A2F]" />
              <h2 className="font-display text-3xl text-[#4B3A2F]">
                Subscribers
              </h2>
            </div>
            <p className="font-script text-xl text-[#4B3A2F] mb-1">
              everyone who&rsquo;s pulled up a rocking chair
            </p>
            <p className="text-sm text-[#6D5A50] italic max-w-md">
              Search, sort, filter, mark unsubscribed, delete, and export a
              CSV of your subscriber list. New signups appear here
              automatically.
            </p>
          </div>
          <a
            href="/admin/subscribers"
            data-testid="admin-subscribers-open-btn"
            className="btn-pasture inline-flex items-center gap-2"
          >
            <Users size={16} />
            Open Subscribers
          </a>
        </section>

        {/* QR Code Maker — link out to the printable QR tool */}
        <section
          data-testid="admin-qr-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <div className="flex items-baseline gap-3 mb-1">
              <QrCode size={20} className="text-[#4B3A2F]" />
              <h2 className="font-display text-3xl text-[#4B3A2F]">
                QR Code Maker
              </h2>
            </div>
            <p className="font-script text-xl text-[#4B3A2F] mb-1">
              print pretty QRs for anywhere you like
            </p>
            <p className="text-sm text-[#6D5A50] italic max-w-md">
              Pick a destination on the site, tweak the label, download a
              1200 × 1200 PNG with the Salty Moo frame around it. Great for
              book covers, tags, and business cards.
            </p>
          </div>
          <a
            href="/qr-maker"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-qr-open-btn"
            className="btn-pasture inline-flex items-center gap-2"
          >
            <QrCode size={16} />
            Open QR Maker
          </a>
        </section>

        {/* Welcome Email editor */}
        <section
          data-testid="admin-welcome-email-section"
          className="parchment-deep page-shadow border border-[#E7DFD5] rounded-md p-8 mt-12"
        >
          <div className="flex items-baseline gap-3 mb-1">
            <Mail size={20} className="text-[#4B3A2F]" />
            <h2 className="font-display text-3xl text-[#4B3A2F]">
              Welcome Email
            </h2>
          </div>
          <p className="font-script text-xl text-[#4B3A2F] mb-6">
            what new subscribers receive
          </p>

          {!welcome ? (
            <p className="text-sm text-[#6D5A50] italic">loading…</p>
          ) : (
            <form onSubmit={saveWelcome} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-2">
                  Subject line
                </label>
                <input
                  data-testid="admin-welcome-subject"
                  className={inputCls}
                  value={welcome.subject}
                  onChange={(e) => setWelcome({ ...welcome, subject: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-2">
                  Headline <span className="text-[#6D5A50] normal-case tracking-normal">(big italic opener)</span>
                </label>
                <input
                  data-testid="admin-welcome-headline"
                  className={inputCls}
                  value={welcome.headline}
                  onChange={(e) => setWelcome({ ...welcome, headline: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-2">
                  Body <span className="text-[#6D5A50] normal-case tracking-normal">(bold, italic, links, bullets — highlight text to format)</span>
                </label>
                <div
                  data-testid="admin-welcome-body-wysiwyg"
                  className="rounded-md border border-[#E7DFD5] bg-[#FBF7F0] overflow-hidden focus-within:border-[#B08A5A]"
                >
                  <EditorProvider>
                    <Editor
                      data-testid="admin-welcome-body"
                      value={welcome.body || ""}
                      onChange={(e) =>
                        setWelcome({ ...welcome, body: e.target.value })
                      }
                      containerProps={{
                        style: {
                          background: "#FBF7F0",
                          border: "none",
                          minHeight: "180px",
                          fontFamily: "Georgia, 'Times New Roman', serif",
                          fontSize: "15px",
                          lineHeight: "1.7",
                          color: "#2C2724",
                        },
                      }}
                    >
                      <Toolbar
                        style={{
                          background: "#F3EBDD",
                          borderBottom: "1px solid #E7DFD5",
                        }}
                      >
                        <BtnBold />
                        <BtnItalic />
                        <BtnUnderline />
                        <Separator />
                        <BtnBulletList />
                        <BtnNumberedList />
                        <Separator />
                        <BtnLink />
                        <BtnClearFormatting />
                      </Toolbar>
                    </Editor>
                  </EditorProvider>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-2">
                    Closing word <span className="text-[#6D5A50] normal-case tracking-normal">(red italic)</span>
                  </label>
                  <input
                    data-testid="admin-welcome-closing"
                    className={inputCls}
                    value={welcome.closing}
                    onChange={(e) => setWelcome({ ...welcome, closing: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-2">
                    Sign-off
                  </label>
                  <input
                    data-testid="admin-welcome-signoff"
                    className={inputCls}
                    value={welcome.signoff}
                    onChange={(e) => setWelcome({ ...welcome, signoff: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  data-testid="admin-welcome-save-btn"
                  type="submit"
                  disabled={savingWelcome}
                  className="btn-pasture disabled:opacity-60"
                >
                  {savingWelcome ? "Saving…" : "Save email copy"}
                </button>
              </div>

              {/* Send a test to any email — great for previewing changes */}
              <div className="mt-2 pt-6 border-t border-[#E7DFD5]">
                <label className="block text-xs uppercase tracking-[0.25em] text-[#4B3A2F] mb-2">
                  Send a test email to preview
                </label>
                <div className="flex flex-wrap gap-3">
                  <input
                    data-testid="admin-welcome-test-email"
                    type="email"
                    placeholder="you@example.com"
                    className={`${inputCls} flex-grow min-w-[220px]`}
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                  <button
                    data-testid="admin-welcome-test-btn"
                    type="button"
                    onClick={sendTest}
                    disabled={sendingTest}
                    className="btn-pasture inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    <Send size={14} />
                    {sendingTest ? "Sending…" : "Send test"}
                  </button>
                </div>
                <p className="text-xs text-[#6D5A50] italic mt-2">
                  Tip: save your changes above first, then send a test so you're previewing the latest copy.
                </p>
              </div>
            </form>
          )}
        </section>
      </div>
      {cropTarget && (
        <CropEditor
          file={cropTarget.file}
          label={cropTarget.name}
          aspect={LANDSCAPE_SLUGS.has(cropTarget.slug) ? 4 / 3 : 1}
          busy={uploadingSlug === cropTarget.slug}
          onCancel={() => setCropTarget(null)}
          onCommit={commitCroppedPhoto}
        />
      )}
    </div>
  );
}
